import { useEffect, useMemo, useRef, useState } from "react";
import {
  Scissors,
  Plus,
  Ban,
  Edit2,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Clock,
  DoorOpen,
  User as UserIcon,
  Phone as PhoneIcon,
  Mail,
  Check,
  X as XIcon,
  CalendarDays,
  Calendar as CalendarIcon,
  List as ListIcon,
  LayoutGrid,
  RefreshCw,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useLocation } from "@/contexts/LocationContext";
import { useSearchParams } from "react-router-dom";
import { useConfirm } from "@/contexts/ConfirmContext";
import { usePermissions } from "@/hooks/usePermissions";

interface ServiceLite {
  id: string;
  name: string;
  color: string;
  durationMinutes: number;
  price: number;
}

interface SpecialistLite {
  id: string;
  firstName: string;
  lastName: string | null;
  color: string;
}

interface Appointment {
  id: string;
  locationId: string;
  date: string; // ISO date
  startTime: string;
  endTime: string;
  durationMinutes: number;
  price: number;
  status: string;
  source: string;
  notes: string | null;
  specialRequests: string | null;
  confirmationCode: string;
  customer: {
    id: string;
    firstName: string;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    countryCode: string;
  };
  specialist: SpecialistLite;
  service: ServiceLite;
  appointmentRoom?: {
    id: string;
    name: string;
    color: string;
  } | null;
  // Solicitud de cambio de día/hora/profesional pedida por el cliente desde
  // el enlace de su cita (18-sep-2026), pendiente de decidir. `changes` viene
  // YA formateado en español desde el backend (`describeAppointmentChanges`):
  // no se recalcula aquí, solo se pinta.
  pendingChangeRequest?: AppointmentPendingChangeRequest | null;
  // "A nombre de" (1-oct-2026): persona a la que va la cita cuando NO es quien
  // reserva (p. ej. un padre que saca cita para su hijo sin registrarlo con su
  // teléfono). El backend decide si difiere de la ficha del cliente
  // (`bookingNameDiffers`): el chip solo se pinta con `true`; si el campo no
  // llega (undefined) no se pinta nada. El front SOLO pinta, no compara nombres.
  bookingName?: string | null;
  bookingNameDiffers?: boolean;
}

interface AppointmentPendingChangeRequest {
  date: string; // "YYYY-MM-DD" pedido
  startTime: string; // "HH:MM" pedido
  specialistId: string;
  specialistName?: string;
  requestedAt: string;
  oldValues: {
    date: string;
    startTime: string;
    endTime: string;
    specialistId: string;
    specialistName?: string;
  };
  changes: string[];
}

interface AppointmentChangeApiResponse {
  success: boolean;
  message?: string;
  code?: string;
  data?: Appointment;
}

interface Specialist {
  id: string;
  firstName: string;
  lastName: string | null;
  color: string;
  locationId: string;
  isActive: boolean;
  services: { serviceId: string }[];
}

interface ServiceFull {
  id: string;
  name: string;
  color: string;
  durationMinutes: number;
  price: number;
  isActive: boolean;
}

interface RoomDetail {
  id: string;
  name: string;
  color: string;
  isActive: boolean;
  services: { service: { id: string; name: string; color: string } }[];
  specialists: {
    specialist: {
      id: string;
      firstName: string;
      lastName: string | null;
      color: string;
    };
  }[];
}

interface DaySchedule {
  dayOfWeek: number;
  isOpen: boolean;
  openTime: string | null;
  closeTime: string | null;
}

const STATUS_META: Record<string, { label: string; bg: string; text: string }> =
  {
    pending: {
      label: "Pendiente",
      bg: "bg-yellow-100",
      text: "text-yellow-800",
    },
    confirmed: {
      label: "Confirmada",
      bg: "bg-blue-100",
      text: "text-blue-800",
    },
    double_confirmed: {
      label: "Doble confirmada",
      bg: "bg-green-100",
      text: "text-green-800",
    },
    arrived: { label: "Llegó", bg: "bg-purple-100", text: "text-purple-800" },
    seated: { label: "En curso", bg: "bg-indigo-100", text: "text-indigo-800" },
    completed: {
      label: "Completada",
      bg: "bg-emerald-100",
      text: "text-emerald-800",
    },
    cancelled: { label: "Cancelada", bg: "bg-red-100", text: "text-red-800" },
    no_show: { label: "No show", bg: "bg-gray-200", text: "text-gray-700" },
  };

const formatDateInput = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const addDays = (date: Date, days: number): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

// Lunes como inicio de semana
const startOfWeek = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0 dom, 1 lun, ...
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

const isSameDate = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const startOfMonth = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(1);
  return d;
};

const endOfMonth = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setMonth(d.getMonth() + 1, 0);
  return d;
};

const addMonths = (date: Date, months: number): Date => {
  const d = new Date(date);
  const targetMonth = d.getMonth() + months;
  d.setDate(1);
  d.setMonth(targetMonth);
  return d;
};

/* ── Buscador global de pacientes (1-oct-2026) ───────────────────────────── */

/** Paciente tal y como lo devuelve el buscador global (`GET /customers`). */
interface PatientLite {
  id: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  countryCode: string;
}

/** `GET /customers?search=…`: según versión llega `data.customers` o `data` directo. */
interface PatientSearchResponse {
  success: boolean;
  data?: { customers?: PatientLite[] } | PatientLite[];
}

type PatientLoadState = "idle" | "loading" | "ready" | "error";

/**
 * Estados que CIERRAN una cita. "Activa" = cualquiera que no sea de estos,
 * igual que `ACTIVE_STATUSES` del backend (pending, confirmed,
 * double_confirmed, arrived, seated). OJO: no basta con pending/confirmed,
 * `double_confirmed` es real: lo pone el propio cliente al confirmar su
 * asistencia desde el enlace de la cita, y esa cita SÍ es la próxima.
 */
const CLOSED_APPOINTMENT_STATUSES = ["completed", "cancelled", "no_show"];

/**
 * Día natural de una cita como "YYYY-MM-DD". `date` es una fecha SIN hora que
 * el servidor devuelve a medianoche UTC: se corta el texto en vez de pasar por
 * `new Date()`, que en zonas con UTC negativo (América) caería en el día
 * anterior. Mismo criterio que `openEdit`.
 */
const apptDayKey = (a: { date: string }): string => a.date.slice(0, 10);

/** "YYYY-MM-DD" -> Date local a medianoche (ese día natural, sin saltos de zona). */
const dayKeyToLocalDate = (key: string): Date => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** "vie 10 oct" (con el año si no es el actual, para que el historial no confunda). */
const formatApptDay = (key: string): string => {
  const d = dayKeyToLocalDate(key);
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    day: "numeric",
    month: "short",
  };
  if (d.getFullYear() !== new Date().getFullYear()) options.year = "numeric";
  // es-ES pone coma tras el día de la semana ("vie, 10 oct"): fuera.
  return d.toLocaleDateString("es-ES", options).replace(",", "");
};

const compareAppointmentsAsc = (a: Appointment, b: Appointment): number =>
  apptDayKey(a).localeCompare(apptDayKey(b)) ||
  a.startTime.localeCompare(b.startTime);

const patientFullName = (p: {
  firstName: string;
  lastName: string | null;
}): string => `${p.firstName} ${p.lastName ?? ""}`.trim();

const patientPhoneLabel = (p: {
  phone: string | null;
  countryCode: string;
}): string => (p.phone ? [p.countryCode, p.phone].filter(Boolean).join(" ") : "");

interface Shift {
  start: string; // "HH:MM"
  end: string; // "HH:MM"
}

/** Franja reservada de la agenda: ni cita ni cliente, solo hora tapada. */
interface BloqueDeAgenda {
  id: string;
  specialistId: string;
  startTime: string;
  endTime: string;
  reason?: string | null;
  /** Quién lo bloqueó, para poder preguntarle. Null en los de Google. */
  authorName?: string | null;
}

interface DayAvailability {
  isWorking: boolean;
  shifts: Shift[]; // tramos efectivos de trabajo (override > base)
  blocks: BloqueDeAgenda[];
  reason?: string;
}

interface FormData {
  date: string;
  startTime: string;
  serviceId: string;
  specialistId: string;
  appointmentRoomId: string;
  durationMinutes: string;
  notes: string;
  /** "A nombre de" (opcional): otra persona distinta de quien reserva. */
  bookingName: string;
  customerSearch: string;
  customerId: string | null;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone: string;
  customerCountryCode: string;
}

const buildEmptyForm = (date: string): FormData => ({
  date,
  startTime: "10:00",
  serviceId: "",
  specialistId: "",
  appointmentRoomId: "",
  durationMinutes: "",
  notes: "",
  bookingName: "",
  customerSearch: "",
  customerId: null,
  customerFirstName: "",
  customerLastName: "",
  customerEmail: "",
  customerPhone: "",
  customerCountryCode: "+34",
});

export default function Appointments() {
  const { currentLocationId, currentLocation } = useLocation();
  const { confirm } = useConfirm();
  const { can } = usePermissions();
  const canManage = can("citas:gestionar");

  // La agenda puede abrirse en un día concreto con ?date=YYYY-MM-DD: es lo que
  // usan los avisos de cita (18-sep-2026). Sin esto, pinchar en "Cita
  // cancelada" abría la agenda de HOY y el equipo no encontraba nada.
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const desde = new URLSearchParams(window.location.search).get("date");
    if (desde && /^\d{4}-\d{2}-\d{2}$/.test(desde)) {
      const [y, m, d] = desde.split("-").map(Number);
      const fecha = new Date(y, m - 1, d);
      if (!Number.isNaN(fecha.getTime())) return fecha;
    }
    return new Date();
  });
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const [services, setServices] = useState<ServiceFull[]>([]);
  const [roomsDetail, setRoomsDetail] = useState<RoomDetail[]>([]);
  const [roomSchedules, setRoomSchedules] = useState<
    Record<string, DaySchedule[]>
  >({});
  const [showRoomsPanel, setShowRoomsPanel] = useState(false);
  const [filterSpecialistId, setFilterSpecialistId] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterRoomId, setFilterRoomId] = useState<string>("");
  const [viewMode, setViewMode] = useState<"list" | "day" | "week" | "month">(
    () => {
      if (typeof window === "undefined") return "day";
      const stored = window.localStorage.getItem("appointments_view");
      if (stored === "calendar") return "day"; // back-compat
      return (stored as "list" | "day" | "week" | "month") || "day";
    },
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("appointments_view", viewMode);
    }
  }, [viewMode]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Modal de confirmación al cambiar de profesional (drag & drop entre columnas)
  const [reassignModal, setReassignModal] = useState<{
    appointment: Appointment;
    fromSpecialist: SpecialistLite;
    toSpecialist: Specialist;
    targetDate: Date;
    startTime: string;
  } | null>(null);
  const [reassignReason, setReassignReason] = useState("");
  const [reassignSubmitting, setReassignSubmitting] = useState(false);

  // Auto-dismiss del toast de éxito tras 3s y del error tras 5s
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(""), 3000);
    return () => clearTimeout(t);
  }, [success]);
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(""), 5000);
    return () => clearTimeout(t);
  }, [error]);

  const [showModal, setShowModal] = useState(false);

  // ── Cita rápida desde la agenda (15-oct-2026, Centro Elle) ────────────────
  // Pinchar un hueco abre una ventana CORTA con el profesional, el día y la
  // hora ya puestos: solo se elige cliente y servicio (la sala se propone
  // sola). Usa el MISMO `formData`, `handleSubmit` y avisos que el formulario
  // completo, al que se pasa con «Más opciones» sin perder nada.
  const [quickCreate, setQuickCreate] = useState(false);
  const [quickNewCustomer, setQuickNewCustomer] = useState(false);
  const [quickRoomPicker, setQuickRoomPicker] = useState(false);
  const [quickServiceFilter, setQuickServiceFilter] = useState("");
  // Abierta sin profesional (vista Semana con todos visibles): se le pregunta.
  const [quickAskSpecialist, setQuickAskSpecialist] = useState(false);
  // Cualquier otra forma de abrir el formulario (botón, editar…) es la completa.
  useEffect(() => {
    if (!showModal) setQuickCreate(false);
  }, [showModal]);

  // ── "Bloquear hueco": reservar una franja SIN crear una cita ──────────────
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blocksReloadToken, setBlocksReloadToken] = useState(0);
  const [blockSaving, setBlockSaving] = useState(false);
  const [blockForm, setBlockForm] = useState({
    specialistIds: [] as string[],
    date: "",
    startTime: "10:00",
    durationMinutes: 60,
    reason: "",
  });

  const [viewingAppointment, setViewingAppointment] =
    useState<Appointment | null>(null);
  // Decisión sobre la solicitud de cambio pendiente (modal de detalle,
  // 18-sep-2026): "reason" es el motivo opcional del rechazo, "action" marca
  // qué botón está en curso (deshabilita ambos mientras dura), "error" es un
  // aviso fijo DENTRO de la tarjeta (no un toast que desaparece a los 5s):
  // el caso más probable es un 409 explicando que el hueco ya no está libre,
  // y la solicitud sigue viva, así que el aviso tiene que seguir a la vista.
  const [changeRequestReason, setChangeRequestReason] = useState("");
  const [changeRequestAction, setChangeRequestAction] = useState<
    "approve" | "reject" | null
  >(null);
  const [changeRequestError, setChangeRequestError] = useState<string | null>(
    null,
  );
  useEffect(() => {
    setChangeRequestReason("");
    setChangeRequestAction(null);
    setChangeRequestError(null);
  }, [viewingAppointment?.id]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(
    buildEmptyForm(formatDateInput(new Date())),
  );

  // Sala propuesta automáticamente (Luis, 21-sep-2026): con servicio,
  // profesional, fecha y hora, el panel pide al servidor la misma sala que
  // asignaría el widget y la marca en el desplegable. El personal puede
  // cambiarla o dejar "Sin asignar"; en cuanto toca el desplegable a mano
  // dejamos de proponer para esa cita. Antes el desplegable nacía en "Sin
  // asignar" y la mitad de las citas del panel se quedaban sin sala.
  const [roomTouched, setRoomTouched] = useState(false);
  const [roomHint, setRoomHint] = useState<string | null>(null);
  useEffect(() => {
    if (!showModal) return;
    // Al abrir: una cita existente que ya tiene sala se respeta tal cual.
    setRoomTouched(editingId !== null && formData.appointmentRoomId !== "");
    setRoomHint(null);
  }, [showModal, editingId]);
  useEffect(() => {
    if (!showModal || roomTouched || roomsDetail.length === 0) return;
    const { serviceId, specialistId, date, startTime } = formData;
    if (!serviceId || !specialistId || !date || !startTime) return;
    const service = services.find((s) => s.id === serviceId);
    const duration =
      parseInt(formData.durationMinutes, 10) || service?.durationMinutes || 30;
    const [h, m] = startTime.split(":").map((n) => parseInt(n, 10));
    const endMin = (h || 0) * 60 + (m || 0) + duration;
    const endTime = `${String(Math.floor(endMin / 60) % 24).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await apiClient.get<{
          success: boolean;
          data: { room: { id: string; name: string } | null };
        }>("/appointment-rooms/suggest", {
          params: {
            serviceId,
            specialistId,
            date,
            startTime,
            endTime,
            ...(editingId ? { excludeAppointmentId: editingId } : {}),
          },
        });
        if (cancelled) return;
        const room = data.data.room;
        setFormData((prev) => ({ ...prev, appointmentRoomId: room?.id ?? "" }));
        setRoomHint(
          room
            ? `Sala asignada automáticamente (${room.name}) · puedes cambiarla o dejarla sin asignar`
            : "No hay sala libre a esa hora · puedes elegir una o dejarla sin asignar",
        );
      } catch {
        if (!cancelled) setRoomHint(null);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    showModal,
    roomTouched,
    roomsDetail.length,
    services,
    editingId,
    formData.serviceId,
    formData.specialistId,
    formData.date,
    formData.startTime,
    formData.durationMinutes,
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Customer search
  const [customerResults, setCustomerResults] = useState<any[]>([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Horario del centro (Citas → Ajustes): marca las horas que dibuja la agenda.
  // undefined = cargando (así no se pinta 8–22 y salta después); null = sin
  // horario o no se pudo leer → 8–22.
  const [centerSchedule, setCenterSchedule] = useState<
    CenterWeeklySchedule | null | undefined
  >(undefined);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("/appointment-settings")
      .then((res) => {
        if (!cancelled) setCenterSchedule(res.data?.data?.weeklySchedule ?? null);
      })
      .catch(() => {
        if (!cancelled) setCenterSchedule(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentLocationId]);

  // Disponibilidad por especialista
  // weeklySchedules: specialistId -> 7 días con shifts base
  const [weeklySchedules, setWeeklySchedules] = useState<
    Record<string, { dayOfWeek: number; isWorking: boolean; shifts: Shift[] }[]>
  >({});
  // dailyOverrides: `${specialistId}_${YYYY-MM-DD}` -> override
  const [dailyOverrides, setDailyOverrides] = useState<
    Record<string, { isWorking: boolean; shifts: Shift[]; reason?: string }>
  >({});
  // dailyBlocks: `${specialistId}_${YYYY-MM-DD}` -> bloques
  const [dailyBlocks, setDailyBlocks] = useState<
    Record<string, BloqueDeAgenda[]>
  >({});

  useEffect(() => {
    loadCatalog();
  }, [currentLocationId]);

  // Cargar horarios de cada sala de citas
  useEffect(() => {
    if (roomsDetail.length === 0) return;
    let cancelled = false;
    const loadSchedules = async () => {
      const results = await Promise.allSettled(
        roomsDetail.map((r) =>
          apiClient
            .get(`/appointment-rooms/${r.id}/schedule`)
            .then((res) => ({ id: r.id, days: res.data?.data?.days ?? [] }))
            .catch(() => ({ id: r.id, days: [] })),
        ),
      );
      if (cancelled) return;
      const map: Record<string, DaySchedule[]> = {};
      results.forEach((r) => {
        if (r.status === "fulfilled") map[r.value.id] = r.value.days;
      });
      setRoomSchedules(map);
    };
    loadSchedules();
    return () => {
      cancelled = true;
    };
  }, [roomsDetail]);

  // Cargar horario semanal de cada specialist (se hace una vez por carga de especialistas)
  useEffect(() => {
    if (specialists.length === 0) return;
    let cancelled = false;
    const load = async () => {
      try {
        const results = await Promise.all(
          specialists.map((sp) =>
            apiClient
              .get(`/specialists/${sp.id}/schedule`)
              .then((r) => ({ id: sp.id, days: r.data?.data?.days ?? [] }))
              .catch(() => ({ id: sp.id, days: [] })),
          ),
        );
        if (cancelled) return;
        const map: Record<string, any[]> = {};
        results.forEach((r) => (map[r.id] = r.days));
        setWeeklySchedules(map);
      } catch {
        /* noop */
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [specialists]);

  // Cargar overrides + blocks de las fechas visibles para cada specialist
  useEffect(() => {
    if (specialists.length === 0) return;
    let cancelled = false;
    const start =
      viewMode === "week"
        ? startOfWeek(selectedDate)
        : viewMode === "month"
          ? startOfMonth(selectedDate)
          : selectedDate;
    const end =
      viewMode === "week"
        ? addDays(start, 6)
        : viewMode === "month"
          ? endOfMonth(selectedDate)
          : selectedDate;
    const dateFrom = formatDateInput(start);
    const dateTo = formatDateInput(end);
    const load = async () => {
      try {
        const calls: Promise<any>[] = [];
        specialists.forEach((sp) => {
          calls.push(
            apiClient
              .get(`/specialists/${sp.id}/overrides`, {
                params: { dateFrom, dateTo },
              })
              .then((r) => ({
                kind: "ov",
                id: sp.id,
                rows: r.data?.data ?? [],
              }))
              .catch(() => ({ kind: "ov", id: sp.id, rows: [] })),
          );
          calls.push(
            apiClient
              .get(`/specialists/${sp.id}/blocks`, {
                params: { dateFrom, dateTo },
              })
              .then((r) => ({
                kind: "bl",
                id: sp.id,
                rows: r.data?.data ?? [],
              }))
              .catch(() => ({ kind: "bl", id: sp.id, rows: [] })),
          );
        });
        const results = await Promise.all(calls);
        if (cancelled) return;
        const ovs: Record<string, any> = {};
        const bls: Record<string, any[]> = {};
        results.forEach((r: any) => {
          if (r.kind === "ov") {
            r.rows.forEach((o: any) => {
              const dKey = formatDateInput(new Date(o.date));
              ovs[`${r.id}_${dKey}`] = {
                isWorking: o.isWorking,
                shifts: Array.isArray(o.shifts) ? o.shifts : [],
                reason: o.reason,
              };
            });
          } else {
            r.rows.forEach((b: any) => {
              const dKey = formatDateInput(new Date(b.date));
              const k = `${r.id}_${dKey}`;
              if (!bls[k]) bls[k] = [];
              bls[k].push({
                id: b.id,
                specialistId: r.id,
                startTime: b.startTime,
                endTime: b.endTime,
                reason: b.reason,
                authorName: b.user
                  ? `${b.user.firstName}${b.user.lastName ? ` ${b.user.lastName}` : ""}`
                  : null,
              });
            });
          }
        });
        setDailyOverrides(ovs);
        setDailyBlocks(bls);
      } catch {
        /* noop */
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [specialists, selectedDate, viewMode, blocksReloadToken]);

  useEffect(() => {
    loadAppointments();
  }, [
    selectedDate,
    currentLocationId,
    filterSpecialistId,
    filterStatus,
    viewMode,
  ]);

  const loadCatalog = async () => {
    try {
      const [specRes, svcRes, roomsRes] = await Promise.all([
        apiClient.get("/specialists", {
          params: {
            includeInactive: "false",
            ...(currentLocationId ? { locationId: currentLocationId } : {}),
          },
        }),
        apiClient.get("/services", { params: { includeInactive: "false" } }),
        apiClient.get("/appointment-rooms", {
          params: {
            ...(currentLocationId ? { locationId: currentLocationId } : {}),
          },
        }),
      ]);
      setSpecialists(specRes.data?.data ?? []);
      setServices(svcRes.data?.data ?? []);
      const roomsData = roomsRes.data?.data ?? [];
      setRoomsDetail(roomsData);
    } catch (err: any) {
      setError(err?.response?.data?.message || "No se pudo cargar el catálogo");
    }
  };

  const loadAppointments = async () => {
    try {
      setIsLoading(true);
      setError("");
      const params: Record<string, string> = {};
      if (viewMode === "week") {
        const start = startOfWeek(selectedDate);
        const end = addDays(start, 6);
        params.dateFrom = formatDateInput(start);
        params.dateTo = formatDateInput(end);
      } else if (viewMode === "month") {
        const start = startOfMonth(selectedDate);
        const end = endOfMonth(selectedDate);
        params.dateFrom = formatDateInput(start);
        params.dateTo = formatDateInput(end);
      } else {
        params.date = formatDateInput(selectedDate);
      }
      if (currentLocationId) params.locationId = currentLocationId;
      if (filterSpecialistId) params.specialistId = filterSpecialistId;
      if (filterStatus) params.status = filterStatus;
      const res = await apiClient.get("/appointments", { params });
      setAppointments(res.data?.data ?? []);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || "No se pudieron cargar las citas",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Solo filtra por sala. El filtro por texto (cliente/teléfono/email/código/
  // servicio) se quitó el 1-oct-2026: el input de arriba es ahora el buscador
  // global de pacientes (ver más abajo) y ya no recorta las citas del día.
  const filtered = useMemo(() => {
    return appointments.filter((a) => {
      if (filterRoomId) {
        if (filterRoomId === "_unassigned_") {
          if (a.appointmentRoom) return false;
        } else {
          if (a.appointmentRoom?.id !== filterRoomId) return false;
        }
      }
      return true;
    });
  }, [appointments, filterRoomId]);

  // Horas que dibujan las vistas Día y Semana: las del horario del centro,
  // ampliadas si alguna cita visible cae fuera.
  const calendarHourRange = useMemo(
    () => computeCalendarHourRange(centerSchedule ?? null, filtered),
    [centerSchedule, filtered],
  );

  // ── Buscador GLOBAL de pacientes (1-oct-2026) ─────────────────────────────
  // Antes el input de arriba solo filtraba las citas YA cargadas del día/
  // semana visible, así que no encontraba a quien tenía cita otro día. Ahora
  // busca en TODA la base de clientes (mismo endpoint que el typeahead del
  // alta de cita, pero con estado PROPIO: no tocar `customerResults` ni
  // `showCustomerDropdown`, que son del modal de alta) y abre una ventana
  // centrada con sus citas. Pulsar una cita lleva a ella (`goToAppointment`).
  const [patientQuery, setPatientQuery] = useState("");
  const [patientResults, setPatientResults] = useState<PatientLite[]>([]);
  const [patientSearchState, setPatientSearchState] =
    useState<PatientLoadState>("idle");
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const patientSearchRef = useRef<HTMLDivElement | null>(null);

  // La ventana está abierta mientras haya paciente seleccionado.
  const [selectedPatient, setSelectedPatient] = useState<PatientLite | null>(
    null,
  );
  const selectedPatientId = selectedPatient?.id ?? null;
  const [patientAppointments, setPatientAppointments] = useState<Appointment[]>(
    [],
  );
  const [patientApptsState, setPatientApptsState] =
    useState<PatientLoadState>("idle");
  const [patientReloadToken, setPatientReloadToken] = useState(0);

  // Búsqueda con debounce de 300 ms y mínimo 2 caracteres. `cancelled` descarta
  // la respuesta de una búsqueda que la siguiente pulsación ya dejó atrás.
  useEffect(() => {
    const q = patientQuery.trim();
    if (q.length < 2) {
      setPatientResults([]);
      setPatientSearchState("idle");
      return;
    }
    let cancelled = false;
    setPatientSearchState("loading");
    const timer = setTimeout(async () => {
      try {
        const res = await apiClient.get<PatientSearchResponse>("/customers", {
          params: { search: q, limit: 8 },
        });
        if (cancelled) return;
        const payload = res.data?.data;
        const list = Array.isArray(payload)
          ? payload
          : (payload?.customers ?? []);
        setPatientResults(list);
        setPatientSearchState("ready");
      } catch {
        if (cancelled) return;
        setPatientResults([]);
        setPatientSearchState("error");
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [patientQuery]);

  // Cerrar el desplegable al pulsar fuera (ratón o táctil: en iOS Safari un
  // toque sobre una zona no interactiva no genera `mousedown`).
  useEffect(() => {
    if (!showPatientDropdown) return;
    const onOutside = (e: MouseEvent | TouchEvent) => {
      const box = patientSearchRef.current;
      if (box && !box.contains(e.target as Node)) setShowPatientDropdown(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [showPatientDropdown]);

  // Todas las citas del paciente elegido (sin rango de fechas, de la sede
  // actual). Se vuelve a pedir en cada apertura y con "Reintentar".
  useEffect(() => {
    if (!selectedPatientId) return;
    let cancelled = false;
    setPatientApptsState("loading");
    apiClient
      .get<{ success: boolean; data?: Appointment[] }>("/appointments", {
        params: {
          customerId: selectedPatientId,
          ...(currentLocationId ? { locationId: currentLocationId } : {}),
        },
      })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.data;
        setPatientAppointments(Array.isArray(list) ? list : []);
        setPatientApptsState("ready");
      })
      .catch(() => {
        if (!cancelled) setPatientApptsState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPatientId, currentLocationId, patientReloadToken]);

  // Escape cierra la ventana del paciente, igual que la X y el fondo.
  useEffect(() => {
    if (!selectedPatientId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelectedPatient(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedPatientId]);

  // Próxima cita = la más cercana con día >= hoy (día local) que siga ACTIVA.
  // El resto (incluidas las canceladas/completadas), de más reciente a más
  // antigua. Se ordena aquí en vez de fiarse del orden del servidor.
  const patientView = useMemo(() => {
    const todayKey = formatDateInput(new Date());
    const asc = [...patientAppointments].sort(compareAppointmentsAsc);
    const next =
      asc.find(
        (a) =>
          !CLOSED_APPOINTMENT_STATUSES.includes(a.status) &&
          apptDayKey(a) >= todayKey,
      ) ?? null;
    const rest = asc.filter((a) => a.id !== next?.id).reverse();
    return { next, rest };
  }, [patientAppointments]);

  const openPatient = (p: PatientLite) => {
    setShowPatientDropdown(false);
    // Vaciar antes de abrir: así no asoma ni un instante lo del paciente anterior.
    setPatientAppointments([]);
    setPatientApptsState("loading");
    setSelectedPatient(p);
  };

  const closePatient = () => setSelectedPatient(null);

  const clearPatientSearch = () => {
    setPatientQuery("");
    setPatientResults([]);
    setShowPatientDropdown(false);
  };

  // Saltar a una cita desde la ventana del paciente: coloca la agenda en el
  // día de la cita (vista Día) y abre su detalle, que ya trae "Cancelar cita"
  // y "Editar". Se hace con ESTADO DIRECTO, todo en un mismo lote de React, y
  // no por el deep-link ?date=&id= de más abajo: con `v7_startTransition` el
  // cambio de URL llega en un render aparte del `setViewMode`, así que el
  // calendario pedía primero el día antiguo y luego el nuevo (`loadAppointments`
  // no descarta respuestas viejas), el `GET /appointments/:id` del deep-link se
  // cancelaba al cambiar `isLoading` y `?date=` se quedaba pegado en la URL.
  // El detalle sale de la cita ya en memoria (mismo shape que la lista del
  // calendario), sin petición extra.
  const goToAppointment = (appt: Appointment) => {
    const target = dayKeyToLocalDate(apptDayKey(appt));
    setSelectedPatient(null);
    clearPatientSearch();
    setViewMode("day");
    setSelectedDate((prev) => (isSameDate(prev, target) ? prev : target));
    setViewingAppointment(appt);
  };

  // Devuelve la disponibilidad efectiva del especialista en una fecha concreta
  // Override > horario semanal base; añade siempre los blocks puntuales.
  const getDayAvailability = (
    specialistId: string,
    date: Date,
  ): DayAvailability => {
    const dKey = formatDateInput(date);
    const ovKey = `${specialistId}_${dKey}`;
    const blocks = dailyBlocks[ovKey] ?? [];
    const ov = dailyOverrides[ovKey];
    if (ov) {
      return {
        isWorking: ov.isWorking,
        shifts: ov.isWorking ? ov.shifts : [],
        blocks,
        reason: ov.reason,
      };
    }
    const week = weeklySchedules[specialistId] ?? [];
    const day = week.find((d) => d.dayOfWeek === date.getDay());
    if (!day || !day.isWorking) {
      return { isWorking: false, shifts: [], blocks };
    }
    return { isWorking: true, shifts: day.shifts, blocks };
  };

  // Valida si un slot [start, end) cabe en algún shift y no se solapa con bloques
  const validateSlot = (
    specialistId: string,
    date: Date,
    startTime: string,
    durationMinutes: number,
  ): { ok: boolean; reason?: string } => {
    const startMin = parseHHMM(startTime);
    const endMin = startMin + durationMinutes;
    const av = getDayAvailability(specialistId, date);
    if (!av.isWorking || av.shifts.length === 0) {
      return { ok: false, reason: "El profesional no trabaja ese día" };
    }
    const fits = av.shifts.some((s) => {
      const a = parseHHMM(s.start);
      const b = parseHHMM(s.end);
      return startMin >= a && endMin <= b;
    });
    if (!fits) {
      return {
        ok: false,
        reason: "Fuera del horario del profesional",
      };
    }
    const overlaps = av.blocks.some((b) => {
      const a = parseHHMM(b.startTime);
      const c = parseHHMM(b.endTime);
      return startMin < c && endMin > a;
    });
    if (overlaps) {
      return { ok: false, reason: "El profesional tiene un bloqueo aquí" };
    }
    return { ok: true };
  };

  // Specialists filtered by service when creating
  const eligibleSpecialists = useMemo(() => {
    if (!formData.serviceId) return specialists;
    return specialists.filter((sp) =>
      sp.services.some((s) => s.serviceId === formData.serviceId),
    );
  }, [formData.serviceId, specialists]);

  // Servicios filtrados por profesional seleccionado
  const eligibleServices = useMemo(() => {
    if (!formData.specialistId) return services;
    const sp = specialists.find((s) => s.id === formData.specialistId);
    if (!sp) return services;
    const ids = new Set(sp.services.map((s) => s.serviceId));
    return services.filter((s) => ids.has(s.id));
  }, [formData.specialistId, specialists, services]);

  // Salas activas que admiten el servicio y el profesional elegidos (las dos
  // ventanas de cita usan la misma regla).
  const eligibleRooms = useMemo(
    () =>
      roomsDetail
        .filter((r) => r.isActive)
        .filter(
          (r) =>
            (!formData.serviceId ||
              r.services.some((s) => s.service.id === formData.serviceId)) &&
            (!formData.specialistId ||
              r.specialists.some(
                (s) => s.specialist.id === formData.specialistId,
              )),
        ),
    [roomsDetail, formData.serviceId, formData.specialistId],
  );

  // ── Datos de la ventana de cita rápida ──
  const showQuickCreate = showModal && quickCreate && !editingId;
  const quickSpecialist = specialists.find(
    (sp) => sp.id === formData.specialistId,
  );
  const quickService = services.find((s) => s.id === formData.serviceId);
  const quickEndTime = quickService
    ? formatMinutesHHMM(
        parseHHMM(formData.startTime) +
          (parseInt(formData.durationMinutes, 10) ||
            quickService.durationMinutes),
      )
    : null;
  const quickDateText = formData.date
    ? new Date(`${formData.date}T00:00:00`).toLocaleDateString("es-ES", {
        weekday: "long",
        day: "numeric",
        month: "long",
      })
    : "";
  const quickDateLabel =
    quickDateText.charAt(0).toUpperCase() + quickDateText.slice(1);
  const quickRoom = roomsDetail.find(
    (r) => r.id === formData.appointmentRoomId,
  );
  const foldText = (s: string) =>
    s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const quickServices = quickServiceFilter.trim()
    ? eligibleServices.filter((s) =>
        foldText(s.name).includes(foldText(quickServiceFilter.trim())),
      )
    : eligibleServices;
  const quickCustomerReady =
    !!formData.customerId ||
    (formData.customerFirstName.trim() !== "" &&
      formData.customerPhone.trim() !== "");
  const quickMissing = [
    !formData.specialistId && "profesional",
    !quickCustomerReady && "cliente",
    !formData.serviceId && "servicio",
  ].filter(Boolean) as string[];

  // Si cambio de profesional y el servicio actual no está en su lista, lo limpio
  const onSpecialistChange = (specialistId: string) => {
    if (!specialistId) {
      setFormData({ ...formData, specialistId: "" });
      return;
    }
    const sp = specialists.find((s) => s.id === specialistId);
    const stillValid =
      !formData.serviceId ||
      (sp?.services.some((s) => s.serviceId === formData.serviceId) ?? false);
    setFormData({
      ...formData,
      specialistId,
      serviceId: stillValid ? formData.serviceId : "",
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(buildEmptyForm(formatDateInput(selectedDate)));
    setCustomerResults([]);
    setShowCustomerDropdown(false);
    setShowModal(true);
  };

  /** Cita rápida al pinchar un hueco de la agenda (Día o Semana). */
  const openCreateAt = (date: Date, specialistId: string, time: string) => {
    setEditingId(null);
    setFormData({
      ...buildEmptyForm(formatDateInput(date)),
      startTime: time,
      specialistId,
    });
    setCustomerResults([]);
    setShowCustomerDropdown(false);
    setQuickNewCustomer(false);
    setQuickRoomPicker(false);
    setQuickServiceFilter("");
    setQuickAskSpecialist(!specialistId);
    setQuickCreate(true);
    setShowModal(true);
  };

  /** Pasa a "cliente nuevo" aprovechando lo ya escrito en el buscador. */
  const startQuickNewCustomer = () => {
    const query = formData.customerSearch.trim();
    const digits = query.replace(/[\s()+-]/g, "");
    const isPhone = /^\d{6,}$/.test(digits);
    const [first = "", ...rest] = isPhone ? [] : query.split(/\s+/);
    setFormData((prev) => ({
      ...prev,
      customerId: null,
      customerFirstName: first,
      customerLastName: rest.join(" "),
      customerEmail: "",
      customerPhone: isPhone ? digits : "",
    }));
    setShowCustomerDropdown(false);
    setQuickNewCustomer(true);
  };

  /** Abre "Bloquear hueco" con el día que se está viendo y el filtro puesto. */
  const abrirBloqueo = (prefill?: { specialistId?: string; startTime?: string }) => {
    setBlockForm({
      specialistIds: prefill?.specialistId
        ? [prefill.specialistId]
        : filterSpecialistId
          ? [filterSpecialistId]
          : [],
      date: formatDateInput(selectedDate),
      startTime: prefill?.startTime ?? "10:00",
      durationMinutes: 60,
      reason: "",
    });
    setShowBlockModal(true);
  };

  const guardarBloqueo = async () => {
    if (blockForm.specialistIds.length === 0) {
      setError("Elige al menos un profesional");
      return;
    }
    setBlockSaving(true);
    try {
      const [h, m] = blockForm.startTime.split(":").map((n) => parseInt(n, 10));
      const finMin = (h || 0) * 60 + (m || 0) + blockForm.durationMinutes;
      const endTime = `${String(Math.floor(finMin / 60) % 24).padStart(2, "0")}:${String(finMin % 60).padStart(2, "0")}`;
      const { data } = await apiClient.post("/specialists/blocks", {
        specialistIds: blockForm.specialistIds,
        date: blockForm.date,
        startTime: blockForm.startTime,
        endTime,
        reason: blockForm.reason.trim() || null,
      });
      const citas = data?.data?.citasEnLaFranja ?? 0;
      setSuccess(
        citas > 0
          ? `${data.message}. Ojo: ya había ${citas} cita${citas === 1 ? "" : "s"} en esa franja (no se han tocado).`
          : data.message,
      );
      setShowBlockModal(false);
      setBlocksReloadToken((t) => t + 1);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || "No se pudo bloquear el hueco");
    } finally {
      setBlockSaving(false);
    }
  };

  /** Quitar un bloqueo desde la propia agenda (pulsando la franja). */
  const borrarBloqueo = async (bloque: BloqueDeAgenda) => {
    const ok = await confirm({
      title: "¿Liberar este hueco?",
      message: `${bloque.startTime}–${bloque.endTime}${bloque.reason ? ` · ${bloque.reason}` : ""}${
        bloque.authorName ? ` · bloqueado por ${bloque.authorName}` : ""
      }. Volverá a estar disponible para reservas.`,
      confirmText: "Sí, liberar",
    });
    if (!ok) return;
    try {
      await apiClient.delete(
        `/specialists/${bloque.specialistId}/blocks/${bloque.id}`,
      );
      setSuccess("Hueco liberado");
      setBlocksReloadToken((t) => t + 1);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || "No se pudo liberar el hueco");
    }
  };

  // Si llega ?new=1 (p.ej. desde el botón flotante del dashboard), abrir modal
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      openCreate();
      // Limpiar el query param para no reabrir al navegar
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
    // Navegación desde un aviso de cita con la pantalla ya abierta: el estado
    // inicial no se vuelve a calcular, así que hay que mover el día a mano.
    const desde = searchParams.get("date");
    if (desde && /^\d{4}-\d{2}-\d{2}$/.test(desde)) {
      const [y, m, d] = desde.split("-").map(Number);
      const fecha = new Date(y, m - 1, d);
      if (!Number.isNaN(fecha.getTime())) {
        setSelectedDate((actual) =>
          actual.toDateString() === fecha.toDateString() ? actual : fecha,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Abre el detalle de una cita al llegar con "?id=<id>" (enlace de la
  // campanita de solicitudes de cambio, 18-sep-2026): si ya está en la lista
  // cargada para el día actual se usa directamente; si no (p.ej. la cita
  // pedida es de otro día y el día todavía no se ha movido), se pide por id
  // al backend. Espera a que termine la carga en curso (`isLoading`) para no
  // lanzar una petición de más cuando la cita sí va a llegar en la lista.
  // Mismo criterio que usa "Reservations.tsx" para "?id=".
  useEffect(() => {
    const idParam = searchParams.get("id");
    if (!idParam || isLoading) return;

    const clearIdParam = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("id");
          return next;
        },
        { replace: true },
      );
    };

    const inList = appointments.find((a) => a.id === idParam);
    if (inList) {
      setViewingAppointment(inList);
      clearIdParam();
      return;
    }

    let cancelled = false;
    apiClient
      .get<{ success: boolean; data?: Appointment }>(
        `/appointments/${idParam}`,
      )
      .then((response) => {
        if (cancelled) return;
        if (response.data.data) {
          setViewingAppointment(response.data.data);
        } else {
          setError("No se encontró la cita solicitada");
        }
      })
      .catch(() => {
        if (cancelled) return;
        setError("No se pudo abrir la cita solicitada");
      })
      .finally(() => {
        if (!cancelled) clearIdParam();
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isLoading, appointments]);

  const [isSyncingGoogle, setIsSyncingGoogle] = useState(false);

  const syncAllGoogle = async () => {
    if (isSyncingGoogle) return;
    setIsSyncingGoogle(true);
    setError("");
    try {
      const { data } = await apiClient.post("/appointments/sync-google");
      const r = data?.data ?? {};
      const parts: string[] = [];
      parts.push(
        `${r.synced ?? 0}/${r.total ?? 0} profesionales sincronizados`,
      );
      if (r.appointmentsRestored)
        parts.push(`${r.appointmentsRestored} citas restauradas`);
      if (r.blocksCreated) parts.push(`${r.blocksCreated} bloqueos nuevos`);
      if (r.blocksDeleted) parts.push(`${r.blocksDeleted} bloqueos eliminados`);
      if (Array.isArray(r.errors) && r.errors.length > 0)
        parts.push(`${r.errors.length} con error`);
      setSuccess(parts.join(" · "));
      await loadAppointments();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          "No se pudieron sincronizar los calendarios",
      );
    } finally {
      setIsSyncingGoogle(false);
    }
  };

  const openEdit = (a: Appointment) => {
    setEditingId(a.id);
    setFormData({
      date: a.date.slice(0, 10),
      startTime: a.startTime,
      serviceId: a.service.id,
      specialistId: a.specialist.id,
      appointmentRoomId: (a as any).appointmentRoomId ?? "",
      durationMinutes: String(a.durationMinutes),
      notes: a.notes ?? "",
      bookingName: a.bookingName ?? "",
      customerSearch: "",
      customerId: a.customer.id,
      customerFirstName: a.customer.firstName,
      customerLastName: a.customer.lastName ?? "",
      customerEmail: a.customer.email ?? "",
      customerPhone: a.customer.phone ?? "",
      customerCountryCode: a.customer.countryCode || "+34",
    });
    setCustomerResults([]);
    setShowCustomerDropdown(false);
    setShowModal(true);
  };

  // Search customers
  const searchCustomers = async (q: string) => {
    if (!q || q.trim().length < 2) {
      setCustomerResults([]);
      setShowCustomerDropdown(false);
      return;
    }
    try {
      const res = await apiClient.get("/customers", {
        params: { search: q, limit: 8 },
      });
      const data = res.data?.data?.customers ?? res.data?.data ?? [];
      setCustomerResults(Array.isArray(data) ? data : []);
      setShowCustomerDropdown(true);
    } catch {
      // silent
    }
  };

  const selectCustomer = (c: any) => {
    setFormData((prev) => ({
      ...prev,
      customerId: c.id,
      customerFirstName: c.firstName ?? "",
      customerLastName: c.lastName ?? "",
      customerEmail: c.email ?? "",
      customerPhone: c.phone ?? "",
      customerCountryCode: c.countryCode || "+34",
      customerSearch: "",
    }));
    setShowCustomerDropdown(false);
    setCustomerResults([]);
  };

  const onServiceChange = (serviceId: string) => {
    const svc = services.find((s) => s.id === serviceId);
    setFormData((prev) => ({
      ...prev,
      serviceId,
      durationMinutes: svc ? String(svc.durationMinutes) : prev.durationMinutes,
      // si el specialist actual no ofrece este servicio, se resetea
      specialistId:
        prev.specialistId &&
        specialists
          .find((sp) => sp.id === prev.specialistId)
          ?.services.some((s) => s.serviceId === serviceId)
          ? prev.specialistId
          : "",
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.serviceId) {
      setError("Selecciona un servicio");
      return;
    }
    if (!formData.specialistId) {
      setError("Selecciona un profesional");
      return;
    }
    if (!editingId) {
      // En creación validamos cliente
      if (!formData.customerId) {
        if (!formData.customerFirstName.trim()) {
          setError("Nombre del cliente obligatorio");
          return;
        }
        if (!formData.customerPhone.trim()) {
          setError("Teléfono del cliente obligatorio");
          return;
        }
      }
    }

    try {
      setIsSubmitting(true);
      if (editingId) {
        const payload: any = {
          date: formData.date,
          startTime: formData.startTime,
          serviceId: formData.serviceId,
          specialistId: formData.specialistId,
          notes: formData.notes.trim() || null,
          appointmentRoomId: formData.appointmentRoomId || null,
          // "A nombre de" al EDITAR: vacío = null para BORRARLO en el servidor
          // (el update del backend acepta null y lo limpia). Con undefined el
          // PUT parcial conservaría el valor anterior y no se podría quitar.
          bookingName: formData.bookingName.trim() || null,
        };
        if (formData.durationMinutes) {
          payload.durationMinutes = parseInt(formData.durationMinutes, 10);
        }
        await apiClient.put(`/appointments/${editingId}`, payload);
        setSuccess("Cita actualizada");
      } else {
        if (!currentLocationId) {
          setError("Selecciona una ubicación primero");
          setIsSubmitting(false);
          return;
        }
        const payload: any = {
          locationId: currentLocationId,
          serviceId: formData.serviceId,
          specialistId: formData.specialistId,
          date: formData.date,
          startTime: formData.startTime,
          source: "dashboard",
          notes: formData.notes.trim() || null,
          appointmentRoomId: formData.appointmentRoomId || null,
          // "A nombre de": vacío = se omite la clave (nunca cadena vacía).
          bookingName: formData.bookingName.trim() || undefined,
          customer: formData.customerId
            ? { id: formData.customerId }
            : {
                firstName: formData.customerFirstName.trim(),
                lastName: formData.customerLastName.trim() || null,
                email: formData.customerEmail.trim() || null,
                phone: formData.customerPhone.trim(),
                countryCode: formData.customerCountryCode || "+34",
              },
        };
        if (formData.durationMinutes) {
          payload.durationMinutes = parseInt(formData.durationMinutes, 10);
        }
        // Avisos que el personal puede saltarse: el servidor responde 409 con
        // un código, se pregunta y se reintenta con la marca correspondiente.
        // Pueden encadenarse (cita duplicada + tope semanal), de ahí el bucle;
        // cada aviso se pregunta UNA sola vez: si vuelve a salir, es un error.
        let confirmarDuplicado = false;
        // Opt-in: el panel pide al servidor que le avise del tope semanal por
        // cliente. Tras aceptar el aviso se reenvía SIN la marca y se crea.
        let avisarTopeSemanal = true;
        for (;;) {
          try {
            await apiClient.post("/appointments", {
              ...payload,
              ...(confirmarDuplicado ? { confirmarDuplicado: true } : {}),
              ...(avisarTopeSemanal ? { avisarTopeSemanal: true } : {}),
            });
            break;
          } catch (err: any) {
            const code = err?.response?.data?.code;
            if (code === "CUSTOMER_DOUBLE_BOOKED" && !confirmarDuplicado) {
              // La misma persona ya tiene otra cita a esa hora: se pregunta en
              // vez de prohibir. Si lo que quiere es taparse la hora, el aviso
              // le recuerda que existe "Bloquear hueco" (Centro Elle,
              // 23-sep-2026).
              const otra = err.response.data.data ?? {};
              const seguir = await confirm({
                title: "Esta persona ya tiene otra cita a esa hora",
                message: `${otra.startTime}–${otra.endTime} con ${otra.specialistName} (${otra.serviceName}). Si solo quieres reservarle la hora, cierra esto y usa "Bloquear hueco". ¿Creo la cita igualmente?`,
                confirmText: "Sí, crear igualmente",
              });
              if (!seguir) {
                setIsSubmitting(false);
                return;
              }
              confirmarDuplicado = true;
              continue;
            }
            if (code === "WEEKLY_LIMIT_REACHED" && avisarTopeSemanal) {
              // Tope de citas por cliente y semana (Ajustes de citas): en el
              // widget es un tope duro; aquí decide el personal (Centro Elle,
              // 3-oct-2026).
              const tope = err.response.data.data ?? {};
              const detalle =
                typeof tope.count === "number" && typeof tope.limit === "number"
                  ? `Ya tiene ${tope.count} ${tope.count === 1 ? "cita" : "citas"} en esa semana y el máximo configurado es ${tope.limit}.`
                  : "Ya ha llegado a su máximo de citas en esa semana.";
              const seguir = await confirm({
                title: "Este cliente ha llegado a su tope semanal",
                message: `${detalle} ¿Creo la cita igualmente?`,
                confirmText: "Sí, crear igualmente",
              });
              if (!seguir) {
                setIsSubmitting(false);
                return;
              }
              avisarTopeSemanal = false;
              continue;
            }
            throw err;
          }
        }
        setSuccess("Cita creada");
      }
      setShowModal(false);
      await loadAppointments();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === "ROOM_CONFLICT") {
        setError(
          "Esta sala ya tiene otra cita en ese horario. Elige otra sala u otro horario.",
        );
      } else if (code === "SLOT_TAKEN") {
        setError("El profesional ya tiene otra cita en ese horario.");
      } else {
        setError(err?.response?.data?.message || "No se pudo guardar la cita");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateStatus = async (a: Appointment, status: string) => {
    try {
      setUpdatingId(a.id);
      await apiClient.put(`/appointments/${a.id}`, { status });
      await loadAppointments();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          "No se pudo cambiar el estado de la cita",
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const applyMove = async (
    a: Appointment,
    payload: {
      date?: string;
      specialistId?: string;
      startTime: string;
      notes?: string;
    },
  ) => {
    const previous = appointments;
    const duration =
      parseHHMM(a.endTime) - parseHHMM(a.startTime) ||
      a.service.durationMinutes;
    const newEndMin = parseHHMM(payload.startTime) + duration;
    const newEndTime = `${String(Math.floor(newEndMin / 60)).padStart(2, "0")}:${String(newEndMin % 60).padStart(2, "0")}`;
    setAppointments((prev) =>
      prev.map((x) =>
        x.id === a.id
          ? {
              ...x,
              date: payload.date
                ? new Date(payload.date + "T00:00:00").toISOString()
                : x.date,
              startTime: payload.startTime,
              endTime: newEndTime,
              notes: payload.notes !== undefined ? payload.notes : x.notes,
              specialist: payload.specialistId
                ? (specialists.find((s) => s.id === payload.specialistId) ??
                  x.specialist)
                : x.specialist,
            }
          : x,
      ),
    );
    try {
      await apiClient.put(`/appointments/${a.id}`, payload);
      setSuccess("Cita movida");
    } catch (err: any) {
      setAppointments(previous);
      setError(err?.response?.data?.message || "No se pudo mover la cita");
      throw err;
    }
  };

  const moveAppointment = async (
    a: Appointment,
    payload: { date?: string; specialistId?: string; startTime: string },
  ) => {
    const targetSpecialistId = payload.specialistId ?? a.specialist.id;
    const targetDate = payload.date
      ? new Date(payload.date + "T00:00:00")
      : new Date(a.date);

    // Validación local previa: no permitir mover a horario fuera de turno o bloqueado
    const duration =
      parseHHMM(a.endTime) - parseHHMM(a.startTime) ||
      a.service.durationMinutes;
    const check = validateSlot(
      targetSpecialistId,
      targetDate,
      payload.startTime,
      duration,
    );
    if (!check.ok) {
      setError(check.reason || "No se puede mover ahí");
      return;
    }

    // Si cambia el profesional → confirmación + traza en notas
    const isSpecialistChange =
      payload.specialistId && payload.specialistId !== a.specialist.id;
    if (isSpecialistChange) {
      const toSp = specialists.find((s) => s.id === payload.specialistId);
      if (!toSp) {
        setError("Profesional destino no encontrado");
        return;
      }
      setReassignReason("");
      setReassignModal({
        appointment: a,
        fromSpecialist: a.specialist,
        toSpecialist: toSp,
        targetDate,
        startTime: payload.startTime,
      });
      return;
    }

    await applyMove(a, payload).catch(() => {});
  };

  const confirmReassign = async () => {
    if (!reassignModal) return;
    const { appointment, fromSpecialist, toSpecialist, targetDate, startTime } =
      reassignModal;
    const fromName =
      `${fromSpecialist.firstName} ${fromSpecialist.lastName ?? ""}`.trim();
    const toName =
      `${toSpecialist.firstName} ${toSpecialist.lastName ?? ""}`.trim();
    const ts = new Date();
    const stamp = `${String(ts.getDate()).padStart(2, "0")}/${String(ts.getMonth() + 1).padStart(2, "0")}/${ts.getFullYear()} ${String(ts.getHours()).padStart(2, "0")}:${String(ts.getMinutes()).padStart(2, "0")}`;
    const reason = reassignReason.trim();
    const traceLine = `[${stamp}] Reasignado de ${fromName} a ${toName}${reason ? ` — Motivo: ${reason}` : ""}`;
    const newNotes = appointment.notes
      ? `${appointment.notes}\n${traceLine}`
      : traceLine;

    setReassignSubmitting(true);
    try {
      await applyMove(appointment, {
        date: formatDateInput(targetDate),
        specialistId: toSpecialist.id,
        startTime,
        notes: newNotes,
      });
      setReassignModal(null);
      setReassignReason("");
    } catch {
      // applyMove ya gestiona el toast de error
    } finally {
      setReassignSubmitting(false);
    }
  };

  const handleDelete = async (a: Appointment) => {
    if (
      !(await confirm({
        title: `¿Eliminar la cita de ${a.customer.firstName}?`,
        message: "Esta acción no se puede deshacer.",
        confirmText: "Sí, eliminar",
        danger: true,
      }))
    )
      return;
    try {
      setDeletingId(a.id);
      await apiClient.delete(`/appointments/${a.id}`);
      setSuccess("Cita eliminada");
      await loadAppointments();
    } catch (err: any) {
      setError(err?.response?.data?.message || "No se pudo eliminar la cita");
    } finally {
      setDeletingId(null);
    }
  };

  // Decidir la solicitud de cambio de día/hora/profesional que pidió el
  // cliente (modal de detalle, 18-sep-2026). El caso a cuidar es el 409 al
  // aprobar: el backend vuelve a comprobar el hueco en el momento de decidir
  // y, si ya no está libre, NO toca nada y la solicitud sigue pendiente — el
  // mensaje (explícito, en español) se pinta tal cual dentro de la propia
  // tarjeta, nunca como un error genérico ni como un toast que desaparece
  // solo. Solo se limpia `pendingChangeRequest` en el camino de éxito.
  const handleApprovePendingChange = async () => {
    if (!viewingAppointment?.pendingChangeRequest) return;
    setChangeRequestAction("approve");
    setChangeRequestError(null);
    const appointmentId = viewingAppointment.id;
    try {
      await apiClient.post<AppointmentChangeApiResponse>(
        `/appointments/${appointmentId}/approve-change`,
      );
      setSuccess("Cambio aprobado");
      // Se recarga la cita COMPLETA (no solo la respuesta del approve, que
      // trae "specialist"/"service" recortados sin color): así el detalle
      // sigue mostrando bien el profesional/servicio tras el cambio.
      try {
        const fresh = await apiClient.get<{
          success: boolean;
          data?: Appointment;
        }>(`/appointments/${appointmentId}`);
        if (fresh.data.data) {
          setViewingAppointment(fresh.data.data);
        } else {
          setViewingAppointment((prev) =>
            prev ? { ...prev, pendingChangeRequest: null } : prev,
          );
        }
      } catch {
        setViewingAppointment((prev) =>
          prev ? { ...prev, pendingChangeRequest: null } : prev,
        );
      }
      await loadAppointments();
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      setChangeRequestError(
        axiosError.response?.data?.message || "No se pudo aprobar el cambio",
      );
    } finally {
      setChangeRequestAction(null);
    }
  };

  const handleRejectPendingChange = async () => {
    if (!viewingAppointment?.pendingChangeRequest) return;
    setChangeRequestAction("reject");
    setChangeRequestError(null);
    const appointmentId = viewingAppointment.id;
    try {
      await apiClient.post(`/appointments/${appointmentId}/reject-change`, {
        reason: changeRequestReason.trim() || undefined,
      });
      setSuccess("Solicitud de cambio rechazada");
      setChangeRequestReason("");
      setViewingAppointment((prev) =>
        prev ? { ...prev, pendingChangeRequest: null } : prev,
      );
      await loadAppointments();
    } catch (error: unknown) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      setChangeRequestError(
        axiosError.response?.data?.message || "No se pudo rechazar el cambio",
      );
    } finally {
      setChangeRequestAction(null);
    }
  };

  const dateLabel = useMemo(() => {
    if (viewMode === "week") {
      const start = startOfWeek(selectedDate);
      const end = addDays(start, 6);
      const startStr = start.toLocaleDateString("es-ES", {
        day: "numeric",
        month: "short",
      });
      const endStr = end.toLocaleDateString("es-ES", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      return `${startStr} – ${endStr}`;
    }
    if (viewMode === "month") {
      return selectedDate.toLocaleDateString("es-ES", {
        month: "long",
        year: "numeric",
      });
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sel = new Date(selectedDate);
    sel.setHours(0, 0, 0, 0);
    const diff = Math.round(
      (sel.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
    if (diff === 0) return "Hoy";
    if (diff === 1) return "Mañana";
    if (diff === -1) return "Ayer";
    return selectedDate.toLocaleDateString("es-ES", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  }, [selectedDate, viewMode]);

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Scissors className="w-6 h-6 text-indigo-600" />
            Citas
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Agenda de citas{" "}
            {currentLocation ? (
              <span className="text-gray-700">· {currentLocation.name}</span>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-300 bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => setViewMode("day")}
              className={`px-3 py-2 text-sm inline-flex items-center gap-1 ${
                viewMode === "day"
                  ? "bg-indigo-600 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              title="Vista día"
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="hidden sm:inline">Día</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode("week")}
              className={`px-3 py-2 text-sm inline-flex items-center gap-1 border-l border-gray-300 ${
                viewMode === "week"
                  ? "bg-indigo-600 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              title="Vista semana"
            >
              <CalendarDays className="w-4 h-4" />
              <span className="hidden sm:inline">Semana</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode("month")}
              className={`px-3 py-2 text-sm inline-flex items-center gap-1 border-l border-gray-300 ${
                viewMode === "month"
                  ? "bg-indigo-600 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              title="Vista mes"
            >
              <CalendarIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Mes</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`px-3 py-2 text-sm inline-flex items-center gap-1 border-l border-gray-300 ${
                viewMode === "list"
                  ? "bg-indigo-600 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              title="Vista lista"
            >
              <ListIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Lista</span>
            </button>
          </div>
          <button
            onClick={syncAllGoogle}
            disabled={isSyncingGoogle || !canManage}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !canManage
                ? "No tienes permiso para gestionar citas"
                : "Sincronizar todos los Google Calendar conectados"
            }
          >
            {isSyncingGoogle ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Sincronizar Google</span>
          </button>
          <button
            onClick={openCreate}
            disabled={services.length === 0 || specialists.length === 0 || !canManage}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !canManage
                ? "No tienes permiso para gestionar citas"
                : services.length === 0
                  ? "Crea primero un servicio"
                  : specialists.length === 0
                    ? "Crea primero un profesional"
                    : undefined
            }
          >
            <Plus className="w-4 h-4" />
            Nueva cita
          </button>
          {/* Taparse una hora sin crear una cita: no hay clienta, no se manda
              ningún WhatsApp y el widget deja de ofrecer esa hora. Antes había
              que ir a Profesionales → Horario → Bloqueos, y por eso la gente se
              ponía citas a sí misma (Centro Elle, 23-sep-2026). */}
          <button
            onClick={() => abrirBloqueo()}
            disabled={specialists.length === 0 || !canManage}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              !canManage
                ? "No tienes permiso para gestionar citas"
                : specialists.length === 0
                  ? "Crea primero un profesional"
                  : "Reservar una franja sin crear una cita"
            }
          >
            <Ban className="w-4 h-4" />
            Bloquear hueco
          </button>
        </div>
      </div>

      {(services.length === 0 || specialists.length === 0) && !isLoading && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Para empezar a recibir citas necesitas al menos un{" "}
            <a href="/services" className="underline font-medium">
              servicio
            </a>{" "}
            y un{" "}
            <a href="/specialists" className="underline font-medium">
              profesional
            </a>
            .{" "}
            <a href="/appointments/guide" className="underline font-medium">
              Ver la guía rápida
            </a>
          </span>
        </div>
      )}

      {/* Toasts flotantes (bottom-right) */}
      <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 max-w-sm w-[calc(100vw-2rem)] sm:w-auto pointer-events-none">
        {error && (
          <div className="pointer-events-auto bg-red-600 text-white shadow-lg rounded-lg px-4 py-3 flex items-start gap-2 text-sm animate-in slide-in-from-right-5">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button
              onClick={() => setError("")}
              className="text-white/80 hover:text-white"
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
        )}
        {success && (
          <div className="pointer-events-auto bg-emerald-600 text-white shadow-lg rounded-lg px-4 py-3 flex items-start gap-2 text-sm animate-in slide-in-from-right-5">
            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span className="flex-1">{success}</span>
            <button
              onClick={() => setSuccess("")}
              className="text-white/80 hover:text-white"
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Modal: confirmar cambio de profesional */}
      {reassignModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-gray-900">
                Cambio de profesional
              </h3>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm text-gray-700">
              <p>
                Vas a reasignar la cita de{" "}
                <strong>
                  {reassignModal.appointment.customer.firstName}{" "}
                  {reassignModal.appointment.customer.lastName ?? ""}
                </strong>{" "}
                ({reassignModal.appointment.service.name}) de{" "}
                <strong>
                  {reassignModal.fromSpecialist.firstName}{" "}
                  {reassignModal.fromSpecialist.lastName ?? ""}
                </strong>{" "}
                a{" "}
                <strong>
                  {reassignModal.toSpecialist.firstName}{" "}
                  {reassignModal.toSpecialist.lastName ?? ""}
                </strong>
                .
              </p>
              <p className="text-xs text-gray-500">
                El cambio quedará registrado automáticamente en las notas
                internas de la cita para futura trazabilidad.
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Motivo (opcional)
                </label>
                <textarea
                  value={reassignReason}
                  onChange={(e) => setReassignReason(e.target.value)}
                  rows={3}
                  placeholder="Ej: el cliente lo solicitó, baja del profesional, reorganización del turno…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setReassignModal(null);
                  setReassignReason("");
                }}
                disabled={reassignSubmitting}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmReassign}
                disabled={reassignSubmitting}
                className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {reassignSubmitting && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                Confirmar cambio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Panel Vista General de Salas */}
      {roomsDetail.length > 0 && (
        <div className="mb-4 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <button
            onClick={() => setShowRoomsPanel((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <DoorOpen className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-semibold text-gray-800">
                Vista general de salas
              </span>
              <span className="text-xs bg-indigo-100 text-indigo-700 rounded-full px-2 py-0.5">
                {roomsDetail.length} sala{roomsDetail.length !== 1 ? "s" : ""}
              </span>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-gray-500 transition-transform ${showRoomsPanel ? "rotate-180" : ""}`}
            />
          </button>

          {showRoomsPanel && (
            <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 border-t border-gray-100 pt-3">
              {roomsDetail.map((room) => {
                const schedule = roomSchedules[room.id] ?? [];
                const DAY_SHORT = ["D", "L", "M", "X", "J", "V", "S"];
                const orderedDays = [1, 2, 3, 4, 5, 6, 0];

                const isSelected = filterRoomId === room.id;
                return (
                  <div
                    key={room.id}
                    onClick={() => setFilterRoomId(isSelected ? "" : room.id)}
                    className={`rounded-lg border p-3 cursor-pointer transition-all hover:shadow-md ${
                      isSelected
                        ? "border-indigo-500 ring-2 ring-indigo-200 bg-indigo-50"
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    {/* Header sala */}
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: room.color }}
                      />
                      <span className="font-semibold text-sm text-gray-900 truncate">
                        {room.name}
                      </span>
                      {!room.isActive && (
                        <span className="text-xs bg-gray-200 text-gray-500 rounded px-1.5">
                          oculta
                        </span>
                      )}
                    </div>

                    {/* Horario semanal mini */}
                    {schedule.length > 0 && (
                      <div className="flex gap-1 mb-2">
                        {orderedDays.map((dow) => {
                          const day = schedule.find((d) => d.dayOfWeek === dow);
                          const isOpen = day?.isOpen ?? false;
                          return (
                            <div
                              key={dow}
                              title={
                                isOpen
                                  ? `${day?.openTime} - ${day?.closeTime}`
                                  : "Cerrado"
                              }
                              className={`flex flex-col items-center flex-1 rounded py-1 text-xs font-medium ${
                                isOpen
                                  ? "bg-indigo-100 text-indigo-700"
                                  : "bg-gray-200 text-gray-400"
                              }`}
                            >
                              <span>{DAY_SHORT[dow]}</span>
                              {isOpen && (
                                <span className="text-[9px] leading-tight">
                                  {day?.openTime?.slice(0, 5)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Servicios */}
                    {room.services.length > 0 && (
                      <div className="mb-1.5">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium mb-1">
                          Servicios
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {room.services.map((rs) => (
                            <span
                              key={rs.service.id}
                              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border"
                              style={{
                                backgroundColor: rs.service.color + "22",
                                borderColor: rs.service.color + "66",
                                color: rs.service.color,
                              }}
                            >
                              <span
                                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: rs.service.color }}
                              />
                              {rs.service.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Profesionales */}
                    {room.specialists.length > 0 && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium mb-1">
                          Profesionales
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {room.specialists.map((rs) => (
                            <span
                              key={rs.specialist.id}
                              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                              style={{
                                backgroundColor: rs.specialist.color + "22",
                                color: rs.specialist.color,
                              }}
                            >
                              <span
                                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: rs.specialist.color }}
                              />
                              {rs.specialist.firstName}{" "}
                              {rs.specialist.lastName ?? ""}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sin asignaciones */}
                    {room.services.length === 0 &&
                      room.specialists.length === 0 && (
                        <p className="text-xs text-gray-400 italic">
                          Sin servicios ni profesionales asignados
                        </p>
                      )}
                  </div>
                );
              })}
            </div>
          )}

          {showRoomsPanel && filterRoomId && (
            <div className="px-4 pb-3 flex items-center gap-2 text-xs border-t border-gray-100 pt-2">
              <span className="text-gray-600">
                Filtrando calendario por{" "}
                {filterRoomId === "_unassigned_"
                  ? "citas sin sala"
                  : `sala "${roomsDetail.find((r) => r.id === filterRoomId)?.name ?? ""}"`}
              </span>
              <button
                onClick={() => setFilterRoomId("")}
                className="px-2 py-0.5 bg-gray-200 hover:bg-gray-300 rounded-full text-gray-700"
              >
                ✕ Quitar filtro
              </button>
            </div>
          )}
        </div>
      )}

      {/* Date selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 md:p-4 mb-4">
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={() => {
              if (viewMode === "month") {
                setSelectedDate(addMonths(selectedDate, -1));
              } else {
                setSelectedDate(
                  addDays(selectedDate, viewMode === "week" ? -7 : -1),
                );
              }
            }}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
            aria-label={
              viewMode === "month"
                ? "Mes anterior"
                : viewMode === "week"
                  ? "Semana anterior"
                  : "Día anterior"
            }
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 flex-1 justify-center">
            <CalendarDays className="w-5 h-5 text-indigo-600" />
            <span className="font-semibold text-gray-900 capitalize">
              {dateLabel}
            </span>
            <input
              type="date"
              value={formatDateInput(selectedDate)}
              onChange={(e) => {
                if (e.target.value) {
                  setSelectedDate(new Date(e.target.value + "T00:00:00"));
                }
              }}
              className="ml-2 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={() => {
              if (viewMode === "month") {
                setSelectedDate(addMonths(selectedDate, 1));
              } else {
                setSelectedDate(
                  addDays(selectedDate, viewMode === "week" ? 7 : 1),
                );
              }
            }}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
            aria-label={
              viewMode === "month"
                ? "Mes siguiente"
                : viewMode === "week"
                  ? "Semana siguiente"
                  : "Día siguiente"
            }
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 justify-center sm:justify-start">
          <button
            onClick={() => setSelectedDate(new Date())}
            className="text-xs px-2.5 py-1 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Hoy
          </button>
          <button
            onClick={() => setSelectedDate(addDays(new Date(), 1))}
            className="text-xs px-2.5 py-1 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Mañana
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 md:p-4 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Buscador global de pacientes: busca en toda la base de clientes (no
            solo en las citas del día que se ve) y abre la ventana del paciente. */}
        <div className="relative sm:col-span-1" ref={patientSearchRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={patientQuery}
            onChange={(e) => {
              setPatientQuery(e.target.value);
              setShowPatientDropdown(true);
            }}
            onFocus={() => {
              if (patientQuery.trim().length >= 2) setShowPatientDropdown(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") setShowPatientDropdown(false);
            }}
            placeholder="Buscar paciente (nombre, teléfono o email)…"
            aria-label="Buscar paciente"
            autoComplete="off"
            className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm truncate focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {patientSearchState === "loading" ? (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
          ) : patientQuery ? (
            <button
              type="button"
              onClick={clearPatientSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              aria-label="Borrar búsqueda"
            >
              <XIcon className="w-4 h-4" />
            </button>
          ) : null}
          {showPatientDropdown && patientQuery.trim().length >= 2 && (
            <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
              {patientResults.length > 0 ? (
                patientResults.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => openPatient(p)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900 truncate">
                      {patientFullName(p) || "Paciente sin nombre"}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {patientPhoneLabel(p) || "Sin teléfono"}
                      {p.email ? ` · ${p.email}` : ""}
                    </div>
                  </button>
                ))
              ) : patientSearchState === "error" ? (
                <div className="px-3 py-3 text-sm text-red-600 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>No se pudo buscar. Inténtalo de nuevo.</span>
                </div>
              ) : patientSearchState === "ready" ? (
                <div className="px-3 py-3 text-sm text-gray-500">
                  Ningún paciente coincide con «{patientQuery.trim()}»
                </div>
              ) : (
                <div className="px-3 py-3 text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Buscando…
                </div>
              )}
            </div>
          )}
        </div>
        <select
          value={filterSpecialistId}
          onChange={(e) => setFilterSpecialistId(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">Todos los profesionales</option>
          {specialists.map((sp) => (
            <option key={sp.id} value={sp.id}>
              {sp.firstName} {sp.lastName ?? ""}
            </option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_META).map(([code, meta]) => (
            <option key={code} value={code}>
              {meta.label}
            </option>
          ))}
        </select>
      </div>

      {/* List / Calendar */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ||
        (centerSchedule === undefined &&
          (viewMode === "day" || viewMode === "week")) ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
          </div>
        ) : viewMode === "day" ? (
          <DayCalendar
            date={selectedDate}
            appointments={filtered}
            specialists={
              filterSpecialistId
                ? specialists.filter((s) => s.id === filterSpecialistId)
                : specialists
            }
            onSelectAppointment={setViewingAppointment}
            statusMeta={STATUS_META}
            getAvailability={getDayAvailability}
            hourRange={calendarHourRange}
            onBlockClick={(b) => void borrarBloqueo(b)}
            canManage={canManage}
            onMoveAppointment={(a, specialistId, time) =>
              moveAppointment(a, { specialistId, startTime: time })
            }
            onCreateAt={(specialistId, time) =>
              openCreateAt(selectedDate, specialistId, time)
            }
          />
        ) : viewMode === "week" ? (
          <WeekCalendar
            weekStart={startOfWeek(selectedDate)}
            appointments={filtered}
            specialists={
              filterSpecialistId
                ? specialists.filter((s) => s.id === filterSpecialistId)
                : specialists
            }
            statusMeta={STATUS_META}
            getAvailability={getDayAvailability}
            hourRange={calendarHourRange}
            onSelectAppointment={(a) => setViewingAppointment(a)}
            onCreateAt={canManage ? openCreateAt : undefined}
            onSelectDay={(d) => {
              setSelectedDate(d);
              setViewMode("day");
            }}
          />
        ) : viewMode === "month" ? (
          <MonthCalendar
            month={selectedDate}
            appointments={filtered}
            specialists={
              filterSpecialistId
                ? specialists.filter((s) => s.id === filterSpecialistId)
                : specialists
            }
            statusMeta={STATUS_META}
            onSelectDay={(d) => {
              setSelectedDate(d);
              setViewMode("day");
            }}
          />
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Scissors className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="font-medium text-gray-700">
              {appointments.length === 0
                ? "No hay citas para este día"
                : "Ninguna cita coincide con los filtros"}
            </p>
            {appointments.length === 0 && (
              <p className="text-sm mt-1">
                Crea una nueva cita o cambia de fecha.
              </p>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((a) => {
              const meta = STATUS_META[a.status] ?? {
                label: a.status,
                bg: "bg-gray-100",
                text: "text-gray-700",
              };
              const fullName =
                `${a.customer.firstName} ${a.customer.lastName ?? ""}`.trim();
              const isFinal = ["completed", "cancelled", "no_show"].includes(
                a.status,
              );
              return (
                <li key={a.id} className="p-4 hover:bg-gray-50">
                  <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                    {/* Hora */}
                    <div className="flex md:flex-col items-center md:items-start gap-2 md:gap-0 md:min-w-[88px]">
                      <div className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-1">
                        <Clock className="w-4 h-4 text-gray-400 md:hidden" />
                        {a.startTime}
                      </div>
                      <div className="text-xs text-gray-500">→ {a.endTime}</div>
                    </div>

                    {/* Servicio + profesional */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="text-xs px-2 py-0.5 rounded-full text-white"
                          style={{ backgroundColor: a.service.color }}
                        >
                          {a.service.name}
                        </span>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full text-white inline-flex items-center gap-1"
                          style={{ backgroundColor: a.specialist.color }}
                        >
                          <UserIcon className="w-3 h-3" />
                          {a.specialist.firstName} {a.specialist.lastName ?? ""}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${meta.bg} ${meta.text}`}
                        >
                          {meta.label}
                        </span>
                        {a.pendingChangeRequest && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300 animate-pulse"
                            title="El cliente ha pedido cambiar de día/hora: pendiente de decidir"
                          >
                            <Clock className="w-3 h-3" />
                            Cambio pendiente
                          </span>
                        )}
                        {a.appointmentRoom && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                            style={{
                              backgroundColor: a.appointmentRoom.color + "22",
                              color: a.appointmentRoom.color,
                            }}
                          >
                            <DoorOpen className="w-3 h-3" />
                            {a.appointmentRoom.name}
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          {a.confirmationCode}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        {/* En la agenda manda quien RECIBE la cita: si la cita
                            es "a nombre de" otra persona, ese es el nombre
                            principal y quien reserva queda como contacto. */}
                        <span className="text-sm font-semibold text-gray-900 truncate">
                          {differingBookingName(a) ?? (fullName || "Cliente")}
                        </span>
                        {differingBookingName(a) && (
                          <span className="text-xs text-gray-500 truncate">
                            reserva: {fullName || "Cliente"}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-600 mt-0.5">
                        {a.customer.phone && (
                          <span className="inline-flex items-center gap-1">
                            <PhoneIcon className="w-3 h-3" />
                            {a.customer.countryCode} {a.customer.phone}
                          </span>
                        )}
                        {a.customer.email && (
                          <span className="inline-flex items-center gap-1 truncate">
                            <Mail className="w-3 h-3" />
                            {a.customer.email}
                          </span>
                        )}
                        <span>
                          {a.durationMinutes} min · {a.price.toFixed(2)} €
                        </span>
                      </div>
                      {a.notes && (
                        <div className="text-xs text-gray-500 mt-1 italic truncate">
                          {a.notes}
                        </div>
                      )}
                    </div>

                    {/* Acciones */}
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      {!isFinal && a.status !== "confirmed" && (
                        <button
                          onClick={() => updateStatus(a, "confirmed")}
                          disabled={updatingId === a.id || !canManage}
                          className="text-xs px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Confirmar
                        </button>
                      )}
                      {!isFinal && a.status !== "arrived" && (
                        <button
                          onClick={() => updateStatus(a, "arrived")}
                          disabled={updatingId === a.id || !canManage}
                          className="text-xs px-2 py-1 rounded border border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Llegó
                        </button>
                      )}
                      {!isFinal && (
                        <button
                          onClick={() => updateStatus(a, "completed")}
                          disabled={updatingId === a.id || !canManage}
                          className="text-xs px-2 py-1 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Check className="w-3 h-3" />
                          Completar
                        </button>
                      )}
                      {!isFinal && (
                        <button
                          onClick={() => updateStatus(a, "cancelled")}
                          disabled={updatingId === a.id || !canManage}
                          className="text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <XIcon className="w-3 h-3" />
                          Cancelar
                        </button>
                      )}
                      <button
                        onClick={() => setViewingAppointment(a)}
                        className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                        title="Ver detalle"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(a)}
                        disabled={!canManage}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        title={
                          canManage
                            ? "Eliminar"
                            : "No tienes permiso para gestionar citas"
                        }
                      >
                        {deletingId === a.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Modal */}
      {/* Bloquear hueco: reservar una franja sin crear una cita */}
      {showBlockModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white w-full md:max-w-md md:rounded-xl rounded-t-2xl shadow-xl max-h-[92dvh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900">
                Bloquear hueco
              </h2>
              <button
                onClick={() => setShowBlockModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                Reserva una franja de la agenda sin crear una cita: no hay
                clienta, no se envía ningún WhatsApp y esa hora deja de
                ofrecerse a quien reserve por internet.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profesionales <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {specialists.map((sp) => {
                    const marcado = blockForm.specialistIds.includes(sp.id);
                    return (
                      <button
                        key={sp.id}
                        type="button"
                        onClick={() =>
                          setBlockForm((prev) => ({
                            ...prev,
                            specialistIds: marcado
                              ? prev.specialistIds.filter((id) => id !== sp.id)
                              : [...prev.specialistIds, sp.id],
                          }))
                        }
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                          marcado
                            ? "bg-indigo-600 border-indigo-600 text-white"
                            : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {sp.firstName}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Puedes marcar varios: se bloquea la misma franja en cada
                  agenda.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={blockForm.date}
                    onChange={(e) =>
                      setBlockForm({ ...blockForm, date: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hora <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    step={300}
                    value={blockForm.startTime}
                    onChange={(e) =>
                      setBlockForm({ ...blockForm, startTime: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duración
                </label>
                <select
                  value={blockForm.durationMinutes}
                  onChange={(e) =>
                    setBlockForm({
                      ...blockForm,
                      durationMinutes: parseInt(e.target.value, 10),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {[15, 30, 45, 60, 90, 120, 180, 240, 480].map((min) => (
                    <option key={min} value={min}>
                      {min < 60
                        ? `${min} minutos`
                        : min % 60 === 0
                          ? `${min / 60} hora${min === 60 ? "" : "s"}`
                          : `${Math.floor(min / 60)} h ${min % 60} min`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo{" "}
                  <span className="text-gray-400 font-normal text-xs">
                    (opcional, lo ve solo tu equipo)
                  </span>
                </label>
                <input
                  type="text"
                  value={blockForm.reason}
                  onChange={(e) =>
                    setBlockForm({ ...blockForm, reason: e.target.value })
                  }
                  placeholder="Reservado, comida, formación…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowBlockModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => void guardarBloqueo()}
                  disabled={blockSaving || blockForm.specialistIds.length === 0}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                >
                  {blockSaving ? "Bloqueando…" : "Bloquear"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cita rápida al pinchar un hueco de la agenda (ver `openCreateAt`). */}
      {showQuickCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white w-full md:max-w-md md:rounded-xl rounded-t-2xl shadow-xl max-h-[92dvh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900">Nueva cita</h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-5">
              {/* Lo ya elegido al pinchar: profesional, día y hora */}
              <div className="flex items-center gap-3 rounded-xl bg-indigo-50 px-4 py-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
                  style={{ backgroundColor: quickSpecialist?.color ?? "#9ca3af" }}
                >
                  {quickSpecialist
                    ? quickSpecialist.firstName.charAt(0).toUpperCase()
                    : "?"}
                </div>
                <div className="min-w-0 flex-1">
                  {quickAskSpecialist ? (
                    <select
                      value={formData.specialistId}
                      onChange={(e) => onSpecialistChange(e.target.value)}
                      className="w-full px-2 py-1 border border-indigo-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Elige profesional…</option>
                      {eligibleSpecialists.map((sp) => (
                        <option key={sp.id} value={sp.id}>
                          {sp.firstName} {sp.lastName ?? ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-sm font-semibold text-gray-900 truncate">
                      {quickSpecialist?.firstName} {quickSpecialist?.lastName ?? ""}
                    </div>
                  )}
                  <div className="text-sm text-gray-600">
                    {quickDateLabel} · {formData.startTime}
                    {quickEndTime ? `–${quickEndTime}` : ""}
                  </div>
                </div>
              </div>

              {/* Cliente: buscar o dar de alta con lo mínimo */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Cliente</span>
                  {!formData.customerId && (
                    <button
                      type="button"
                      onClick={() => {
                        if (!quickNewCustomer) {
                          startQuickNewCustomer();
                          return;
                        }
                        // De vuelta al buscador: fuera lo escrito, o se
                        // crearía un cliente que ya no se ve en pantalla.
                        setFormData((prev) => ({
                          ...prev,
                          customerFirstName: "",
                          customerLastName: "",
                          customerPhone: "",
                        }));
                        setQuickNewCustomer(false);
                      }}
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                    >
                      {quickNewCustomer ? "Buscar cliente" : "+ Cliente nuevo"}
                    </button>
                  )}
                </div>
                {formData.customerId ? (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                    <div className="min-w-0 flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {formData.customerFirstName} {formData.customerLastName}
                        </div>
                        {formData.customerPhone && (
                          <div className="text-xs text-gray-500">
                            {formData.customerCountryCode} {formData.customerPhone}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          customerId: null,
                          customerFirstName: "",
                          customerLastName: "",
                          customerEmail: "",
                          customerPhone: "",
                        }))
                      }
                      className="text-xs text-emerald-700 hover:text-emerald-900 underline flex-shrink-0"
                    >
                      cambiar
                    </button>
                  </div>
                ) : quickNewCustomer ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        autoFocus
                        placeholder="Nombre*"
                        value={formData.customerFirstName}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({ ...prev, customerFirstName: value }));
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <input
                        type="text"
                        placeholder="Apellidos"
                        value={formData.customerLastName}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({ ...prev, customerLastName: value }));
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div className="grid grid-cols-[72px_1fr] gap-2">
                      <input
                        type="text"
                        aria-label="Prefijo"
                        value={formData.customerCountryCode}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({ ...prev, customerCountryCode: value }));
                        }}
                        className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <input
                        type="tel"
                        placeholder="Teléfono*"
                        value={formData.customerPhone}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData((prev) => ({ ...prev, customerPhone: value }));
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      autoFocus
                      value={formData.customerSearch}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          customerSearch: value,
                          customerId: null,
                        }));
                        searchCustomers(value);
                      }}
                      placeholder="Buscar por nombre o teléfono…"
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {showCustomerDropdown && customerResults.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                        {customerResults.map((c) => (
                          <button
                            type="button"
                            key={c.id}
                            onClick={() => selectCustomer(c)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">
                              {c.firstName} {c.lastName ?? ""}
                            </div>
                            <div className="text-xs text-gray-500">
                              {c.phone ? `${c.countryCode} ${c.phone}` : ""}
                              {c.email ? ` · ${c.email}` : ""}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {showCustomerDropdown &&
                      customerResults.length === 0 &&
                      formData.customerSearch.trim().length >= 2 && (
                        <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                          <span className="min-w-0 truncate">
                            Ningún cliente con «{formData.customerSearch.trim()}»
                          </span>
                          <button
                            type="button"
                            onClick={startQuickNewCustomer}
                            className="font-medium text-indigo-600 hover:text-indigo-800 flex-shrink-0"
                          >
                            Crearlo como nuevo
                          </button>
                        </div>
                      )}
                  </div>
                )}
              </div>

              {/* Servicio: los del profesional, de un toque (como el widget) */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Servicio</span>
                {eligibleServices.length > 8 && (
                  <input
                    type="text"
                    value={quickServiceFilter}
                    onChange={(e) => setQuickServiceFilter(e.target.value)}
                    placeholder="Filtrar servicios…"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                )}
                <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
                  {quickServices.map((s) => {
                    const selected = s.id === formData.serviceId;
                    return (
                      <button
                        type="button"
                        key={s.id}
                        onClick={() => onServiceChange(s.id)}
                        className={`w-full flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                          selected
                            ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
                            : "border-gray-200 hover:border-indigo-300 hover:bg-gray-50"
                        }`}
                      >
                        <span className="min-w-0 flex items-center gap-2">
                          {selected && (
                            <Check className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                          )}
                          <span className="font-medium text-gray-900 truncate">
                            {s.name}
                          </span>
                        </span>
                        <span className="flex-shrink-0 text-xs text-gray-500">
                          {s.durationMinutes} min · {s.price.toFixed(2)} €
                        </span>
                      </button>
                    );
                  })}
                  {quickServices.length === 0 && (
                    <p className="text-sm text-gray-500 px-1 py-2">
                      {eligibleServices.length === 0
                        ? "Este profesional no tiene servicios asignados."
                        : "Ningún servicio con ese nombre."}
                    </p>
                  )}
                </div>
              </div>

              {/* Sala: la propone el sistema; se cambia solo si hace falta */}
              {roomsDetail.length > 0 && formData.serviceId && (
                <div className="flex items-center gap-2 text-sm">
                  <DoorOpen className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  {quickRoomPicker ? (
                    <select
                      value={formData.appointmentRoomId}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Elección manual: el sistema deja de proponer.
                        setRoomTouched(true);
                        setRoomHint(null);
                        setFormData((prev) => ({ ...prev, appointmentRoomId: value }));
                      }}
                      className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Sin sala</option>
                      {eligibleRooms.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="flex-1 min-w-0 text-gray-700">
                      {quickRoom ? (
                        <>
                          Sala: <strong className="font-medium">{quickRoom.name}</strong>
                          {!roomTouched && (
                            <span className="text-gray-400"> · automática</span>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-500">Sin sala asignada</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setQuickRoomPicker(true)}
                        className="ml-2 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                      >
                        cambiar
                      </button>
                    </span>
                  )}
                </div>
              )}

              {quickMissing.length > 0 && (
                <p className="text-xs text-gray-500 text-right -mb-3">
                  Falta elegir: {quickMissing.join(", ")}
                </p>
              )}
              <div className="flex items-center justify-between gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setQuickCreate(false)}
                  className="text-sm font-medium text-gray-600 hover:text-gray-900"
                  title="Nota interna, «a nombre de», duración y el resto de datos"
                >
                  Más opciones
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !canManage || quickMissing.length > 0}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    Crear cita
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && !showQuickCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          {/* `dvh`, no `vh`: en iOS Safari 100vh incluye lo que tapan las barras
              del navegador, así que con 92vh la hoja crecía por encima del
              viewport visible y la cabecera (con la X) quedaba fuera de alcance. */}
          <div className="bg-white w-full md:max-w-xl md:rounded-xl rounded-t-2xl shadow-xl max-h-[92dvh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? "Editar cita" : "Nueva cita"}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Cliente (solo en creación) */}
              {!editingId && (
                <div className="space-y-2 border-b border-gray-200 pb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Cliente <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.customerSearch}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          customerSearch: e.target.value,
                          customerId: null,
                        });
                        searchCustomers(e.target.value);
                      }}
                      placeholder="Buscar cliente por nombre o teléfono…"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {showCustomerDropdown && customerResults.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                        {customerResults.map((c) => (
                          <button
                            type="button"
                            key={c.id}
                            onClick={() => selectCustomer(c)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">
                              {c.firstName} {c.lastName ?? ""}
                            </div>
                            <div className="text-xs text-gray-500">
                              {c.phone ? `${c.countryCode} ${c.phone}` : ""}
                              {c.email ? ` · ${c.email}` : ""}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {formData.customerId && (
                    <div className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded inline-flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Cliente existente: {formData.customerFirstName}{" "}
                      {formData.customerLastName}
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            customerId: null,
                            customerFirstName: "",
                            customerLastName: "",
                            customerEmail: "",
                            customerPhone: "",
                          })
                        }
                        className="ml-2 text-emerald-700 hover:text-emerald-900 underline"
                      >
                        cambiar
                      </button>
                    </div>
                  )}
                  {!formData.customerId && (
                    <div className="space-y-2 pt-1">
                      <p className="text-xs text-gray-500">
                        O introduce los datos manualmente:
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="Nombre*"
                          value={formData.customerFirstName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              customerFirstName: e.target.value,
                            })
                          }
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <input
                          type="text"
                          placeholder="Apellido"
                          value={formData.customerLastName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              customerLastName: e.target.value,
                            })
                          }
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div className="grid grid-cols-[80px_1fr] gap-2">
                        <input
                          type="text"
                          value={formData.customerCountryCode}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              customerCountryCode: e.target.value,
                            })
                          }
                          className="px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <input
                          type="tel"
                          placeholder="Teléfono*"
                          value={formData.customerPhone}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              customerPhone: e.target.value,
                            })
                          }
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <input
                        type="email"
                        placeholder="Email (opcional)"
                        value={formData.customerEmail}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            customerEmail: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* A nombre de (opcional): la cita es para otra persona distinta
                  de quien reserva (p. ej. un padre que saca cita para su hijo
                  sin registrarlo con su teléfono). Se ve al crear Y al editar:
                  el bloque Cliente de arriba solo existe al crear. */}
              <div>
                <label
                  htmlFor="appointment-booking-name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  A nombre de{" "}
                  <span className="text-gray-400 font-normal text-xs">
                    (opcional)
                  </span>
                </label>
                <input
                  id="appointment-booking-name"
                  type="text"
                  value={formData.bookingName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({ ...prev, bookingName: value }));
                  }}
                  maxLength={120}
                  autoComplete="off"
                  placeholder="Nombre de la persona que recibe la cita"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Si la cita es para otra persona distinta de quien reserva.
                </p>
              </div>

              {/* Profesional */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profesional <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.specialistId}
                  onChange={(e) => onSpecialistChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Selecciona…</option>
                  {eligibleSpecialists.map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.firstName} {sp.lastName ?? ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Servicio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Servicio <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.serviceId}
                  onChange={(e) => onServiceChange(e.target.value)}
                  disabled={!formData.specialistId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="">
                    {formData.specialistId
                      ? eligibleServices.length === 0
                        ? "Este profesional no ofrece servicios"
                        : "Selecciona…"
                      : "Selecciona primero el profesional"}
                  </option>
                  {eligibleServices.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.durationMinutes} min · {s.price.toFixed(2)}{" "}
                      €)
                    </option>
                  ))}
                </select>
              </div>

              {/* Sala (opcional) */}
              {roomsDetail.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sala{" "}
                    <span className="text-gray-400 font-normal text-xs">
                      (opcional)
                    </span>
                  </label>
                  <select
                    value={formData.appointmentRoomId}
                    onChange={(e) => {
                      // Elección manual: el sistema deja de proponer para esta cita.
                      setRoomTouched(true);
                      setRoomHint(null);
                      setFormData({
                        ...formData,
                        appointmentRoomId: e.target.value,
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Sin asignar</option>
                    {eligibleRooms.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                  {roomHint && (
                    <p className="text-xs text-indigo-600 mt-1">{roomHint}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Si no asignas sala, se permitirán múltiples citas en el
                    mismo horario (una por sala disponible).
                  </p>
                </div>
              )}

              {/* Fecha + hora */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hora <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    required
                    value={formData.startTime}
                    onChange={(e) =>
                      setFormData({ ...formData, startTime: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {/* Duración custom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Duración (min) — opcional
                </label>
                <input
                  type="number"
                  min={5}
                  max={480}
                  value={formData.durationMinutes}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      durationMinutes: e.target.value,
                    })
                  }
                  placeholder="Por defecto la del servicio"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Notas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas internas
                </label>
                <textarea
                  rows={2}
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !canManage}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingId ? "Guardar cambios" : "Crear cita"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ventana del paciente (buscador global, 1-oct-2026): CENTRADA a
          propósito, no panel lateral. Se cierra con la X, pulsando el fondo o
          con Escape. `dvh`, no `vh`: en iOS Safari 100vh incluye lo que tapan
          las barras del navegador y la cabecera (con la X) quedaría fuera. */}
      {selectedPatient && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closePatient}
          role="dialog"
          aria-modal="true"
          aria-labelledby="patient-modal-title"
        >
          <div
            className="bg-white rounded-xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cabecera: quién es */}
            <div className="px-5 py-4 border-b flex items-start justify-between gap-3 flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold flex-shrink-0">
                  {(selectedPatient.firstName.charAt(0) || "?").toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Paciente
                  </p>
                  <h2
                    id="patient-modal-title"
                    className="text-lg font-bold text-gray-900 truncate"
                  >
                    {patientFullName(selectedPatient) || "Paciente sin nombre"}
                  </h2>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-600">
                    {patientPhoneLabel(selectedPatient) && (
                      <span className="inline-flex items-center gap-1">
                        <PhoneIcon className="w-3 h-3 flex-shrink-0" />
                        {patientPhoneLabel(selectedPatient)}
                      </span>
                    )}
                    {selectedPatient.email && (
                      <span className="inline-flex items-center gap-1 min-w-0">
                        <Mail className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{selectedPatient.email}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={closePatient}
                className="p-1 hover:bg-gray-100 rounded-lg flex-shrink-0"
                aria-label="Cerrar"
                autoFocus
              >
                <XIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Cuerpo: sus citas (la cabecera queda fija, esto hace scroll) */}
            <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-5">
              {patientApptsState === "loading" ||
              patientApptsState === "idle" ? (
                <div className="py-10 flex flex-col items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  Cargando citas…
                </div>
              ) : patientApptsState === "error" ? (
                <div className="py-8 text-center">
                  <AlertCircle className="w-8 h-8 mx-auto text-red-400 mb-2" />
                  <p className="text-sm text-gray-700">
                    No se pudieron cargar las citas de este paciente.
                  </p>
                  <button
                    type="button"
                    onClick={() => setPatientReloadToken((t) => t + 1)}
                    className="mt-3 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Reintentar
                  </button>
                </div>
              ) : patientAppointments.length === 0 ? (
                <div className="py-10 text-center text-gray-500">
                  <CalendarDays className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                  <p className="font-medium text-gray-700">
                    Este paciente no tiene citas
                  </p>
                  {currentLocation && (
                    <p className="text-xs mt-1">en {currentLocation.name}</p>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1.5 flex items-center gap-1.5">
                      <CalendarDays className="w-3.5 h-3.5" />
                      Próxima cita
                    </p>
                    {patientView.next ? (
                      <PatientAppointmentRow
                        appointment={patientView.next}
                        highlighted
                        onSelect={goToAppointment}
                      />
                    ) : (
                      <div className="rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500">
                        Sin próxima cita
                      </div>
                    )}
                  </div>

                  {patientView.rest.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1.5">
                        Historial de citas ({patientView.rest.length})
                      </p>
                      <div className="space-y-2">
                        {patientView.rest.map((a) => (
                          <PatientAppointmentRow
                            key={a.id}
                            appointment={a}
                            onSelect={goToAppointment}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalle (solo lectura) */}
      {viewingAppointment &&
        (() => {
          const va = viewingAppointment;
          const meta = STATUS_META[va.status] ?? {
            label: va.status,
            bg: "bg-gray-100",
            text: "text-gray-700",
          };
          const isFinal = ["completed", "cancelled", "no_show"].includes(
            va.status,
          );
          return (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setViewingAppointment(null)}
            >
              <div
                className="bg-white rounded-xl max-w-md w-full max-h-[90dvh] overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div
                  className="px-6 py-4 border-b flex items-center justify-between"
                  style={{
                    borderLeftWidth: 4,
                    borderLeftColor: va.specialist.color,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">
                        Cita
                      </span>
                      <span className="font-bold text-gray-900 text-lg">
                        #{va.confirmationCode}
                      </span>
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${meta.bg} ${meta.text}`}
                    >
                      {meta.label}
                    </span>
                  </div>
                  <button
                    onClick={() => setViewingAppointment(null)}
                    className="p-1 hover:bg-gray-100 rounded-lg"
                    aria-label="Cerrar"
                  >
                    <XIcon className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                  {/* Solicitud de cambio pendiente: se decide aquí mismo, es
                      lo primero que se ve al abrir el detalle para que no se
                      pueda pasar por alto. */}
                  {va.pendingChangeRequest && (
                    <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 space-y-3">
                      <p className="text-sm font-bold text-amber-900 flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        Solicitud de cambio pendiente
                      </p>
                      <div className="text-sm text-amber-900 space-y-1 bg-white/60 rounded-lg p-2.5">
                        {va.pendingChangeRequest.changes.length > 0 ? (
                          va.pendingChangeRequest.changes.map((c, i) => {
                            const arrowIdx = c.indexOf("→");
                            if (arrowIdx === -1) {
                              return <p key={i}>{c}</p>;
                            }
                            return (
                              <p key={i}>
                                {c.slice(0, arrowIdx)}→{" "}
                                <strong>{c.slice(arrowIdx + 1).trim()}</strong>
                              </p>
                            );
                          })
                        ) : (
                          <p>
                            Nueva fecha/hora solicitada:{" "}
                            <strong>
                              {va.pendingChangeRequest.date} a las{" "}
                              {va.pendingChangeRequest.startTime}
                            </strong>
                            {va.pendingChangeRequest.specialistName
                              ? ` con ${va.pendingChangeRequest.specialistName}`
                              : ""}
                          </p>
                        )}
                      </div>
                      <p className="text-xs text-amber-700">
                        Pedido el{" "}
                        {new Date(
                          va.pendingChangeRequest.requestedAt,
                        ).toLocaleString("es-ES", {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>

                      {changeRequestError && (
                        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-2.5">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>{changeRequestError}</span>
                        </div>
                      )}

                      {canManage && (
                        <>
                          <input
                            type="text"
                            value={changeRequestReason}
                            onChange={(e) =>
                              setChangeRequestReason(
                                e.target.value.slice(0, 500),
                              )
                            }
                            placeholder="Motivo si lo rechazas (opcional)"
                            maxLength={500}
                            className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleRejectPendingChange}
                              disabled={changeRequestAction !== null}
                              className="flex-1 py-2 px-3 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {changeRequestAction === "reject"
                                ? "Rechazando…"
                                : "Rechazar"}
                            </button>
                            <button
                              type="button"
                              onClick={handleApprovePendingChange}
                              disabled={changeRequestAction !== null}
                              className="flex-1 py-2 px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {changeRequestAction === "approve"
                                ? "Aceptando…"
                                : "Aceptar cambio"}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Cliente */}
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1">
                      Cliente
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold flex-shrink-0">
                        {va.customer.firstName.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 truncate">
                          {va.customer.firstName} {va.customer.lastName ?? ""}
                        </p>
                        {va.customer.phone && (
                          <p className="text-sm text-gray-600">
                            {va.customer.countryCode} {va.customer.phone}
                          </p>
                        )}
                        {va.customer.email && (
                          <p className="text-xs text-gray-500 truncate">
                            {va.customer.email}
                          </p>
                        )}
                        <BookingNameChip appointment={va} className="mt-1.5" />
                      </div>
                    </div>
                  </div>

                  {/* Fecha y hora */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1">
                        Fecha
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(va.date).toLocaleDateString("es-ES", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1">
                        Horario
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {va.startTime} - {va.endTime}
                        <span className="text-gray-500 font-normal ml-1">
                          ({va.durationMinutes}min)
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Servicio */}
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1">
                      Servicio
                    </p>
                    <div className="flex items-center justify-between">
                      <span
                        className="inline-flex items-center gap-2 px-2 py-1 rounded-lg text-sm"
                        style={{
                          backgroundColor: va.service.color + "22",
                          color: va.service.color,
                        }}
                      >
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: va.service.color }}
                        />
                        {va.service.name}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {va.price.toFixed(2)} €
                      </span>
                    </div>
                  </div>

                  {/* Profesional */}
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1">
                      Profesional
                    </p>
                    <span
                      className="inline-flex items-center gap-2 px-2 py-1 rounded-lg text-sm"
                      style={{
                        backgroundColor: va.specialist.color + "22",
                        color: va.specialist.color,
                      }}
                    >
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: va.specialist.color }}
                      />
                      {va.specialist.firstName} {va.specialist.lastName ?? ""}
                    </span>
                  </div>

                  {/* Sala */}
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1">
                      Sala
                    </p>
                    {va.appointmentRoom ? (
                      <span
                        className="inline-flex items-center gap-2 px-2 py-1 rounded-lg text-sm"
                        style={{
                          backgroundColor: va.appointmentRoom.color + "22",
                          color: va.appointmentRoom.color,
                        }}
                      >
                        <DoorOpen className="w-3.5 h-3.5" />
                        {va.appointmentRoom.name}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500 italic">
                        Sin asignar
                      </span>
                    )}
                  </div>

                  {/* Notas internas */}
                  {va.notes && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1">
                        Notas internas
                      </p>
                      <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-2 whitespace-pre-wrap">
                        {va.notes}
                      </p>
                    </div>
                  )}

                  {/* Solicitudes especiales */}
                  {va.specialRequests && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-400 font-medium mb-1">
                        Solicitudes del cliente
                      </p>
                      <p className="text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded-lg p-2 whitespace-pre-wrap">
                        {va.specialRequests}
                      </p>
                    </div>
                  )}

                  {/* Origen */}
                  <div className="pt-2 border-t border-gray-100 text-xs text-gray-500">
                    Origen:{" "}
                    <span className="font-medium text-gray-700 capitalize">
                      {va.source}
                    </span>
                  </div>
                </div>

                {/* Footer acciones */}
                <div className="px-6 py-4 border-t bg-gray-50 flex flex-wrap gap-2 justify-end rounded-b-xl">
                  {!isFinal && (
                    <>
                      <button
                        onClick={async () => {
                          if (!(await confirm({ title: "¿Cancelar esta cita?", message: "La cita quedará marcada como cancelada.", confirmText: "Sí, cancelar", danger: true }))) return;
                          await updateStatus(va, "cancelled");
                          setViewingAppointment(null);
                        }}
                        disabled={!canManage}
                        className="px-3 py-2 text-sm border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancelar cita
                      </button>
                      <button
                        onClick={async () => {
                          await updateStatus(va, "completed");
                          setViewingAppointment(null);
                        }}
                        disabled={!canManage}
                        className="px-3 py-2 text-sm border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Marcar completada
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      const a = va;
                      setViewingAppointment(null);
                      openEdit(a);
                    }}
                    disabled={!canManage}
                    title={canManage ? undefined : "No tienes permiso para gestionar citas"}
                    className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Editar
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}

/* ===================================================================== */
/* "A nombre de …": cita para otra persona (1-oct-2026)                  */
/* ===================================================================== */

/**
 * Nombre de la persona a la que va la cita, SOLO si hay que enseñarlo: el
 * backend marca `bookingNameDiffers` cuando lo tecleado no cuadra con la
 * ficha del cliente (p. ej. un padre que saca cita para su hijo). Con
 * false/undefined (también mientras el backend aún no manda el campo) devuelve
 * null y no se pinta nada, para no duplicar el nombre del propio cliente.
 */
function differingBookingName(
  a: Pick<Appointment, "bookingName" | "bookingNameDiffers">,
): string | null {
  return a.bookingNameDiffers === true && a.bookingName ? a.bookingName : null;
}

/** Chip discreto "A nombre de: X". No pinta nada si no procede. */
function BookingNameChip({
  appointment: a,
  className = "",
}: {
  appointment: Pick<
    Appointment,
    "bookingName" | "bookingNameDiffers" | "customer"
  >;
  className?: string;
}) {
  const name = differingBookingName(a);
  if (!name) return null;
  const onFile = `${a.customer.firstName} ${a.customer.lastName ?? ""}`.trim();
  return (
    <span
      className={`flex w-fit max-w-full items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 ${className}`}
      title={`Cita a nombre de "${name}"${onFile ? ` (quien reserva: ${onFile})` : ""}`}
    >
      <span className="truncate">A nombre de: {name}</span>
    </span>
  );
}

/* ===================================================================== */
/* Fila de cita en la ventana del paciente (buscador global)             */
/* ===================================================================== */

interface PatientAppointmentRowProps {
  appointment: Appointment;
  /** Versión grande y destacada: la "Próxima cita". */
  highlighted?: boolean;
  onSelect: (a: Appointment) => void;
}

function PatientAppointmentRow({
  appointment: a,
  highlighted = false,
  onSelect,
}: PatientAppointmentRowProps) {
  const meta = STATUS_META[a.status] ?? {
    label: a.status,
    bg: "bg-gray-100",
    text: "text-gray-700",
  };
  const specialistName =
    `${a.specialist.firstName} ${a.specialist.lastName ?? ""}`.trim();
  return (
    <button
      type="button"
      onClick={() => onSelect(a)}
      title="Ir a la cita para verla, editarla o cancelarla"
      className={`w-full text-left rounded-lg border transition-colors ${
        highlighted
          ? "px-4 py-3 border-2 border-indigo-300 bg-indigo-50 hover:bg-indigo-100"
          : "px-3 py-2.5 border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className={`font-semibold text-gray-900 ${highlighted ? "text-base" : "text-sm"}`}
        >
          {formatApptDay(apptDayKey(a))} · {a.startTime}
        </span>
        <span
          className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${meta.bg} ${meta.text}`}
        >
          {meta.label}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1.5 min-w-0">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: a.service.color }}
          />
          <span className="truncate">{a.service.name}</span>
        </span>
        <span className="inline-flex items-center gap-1 min-w-0">
          <UserIcon className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{specialistName}</span>
        </span>
        <BookingNameChip appointment={a} />
      </div>
    </button>
  );
}

/* ===================================================================== */
/* Vista Calendario - Día con columnas por profesional                   */
/* ===================================================================== */

// Rango de la agenda cuando el centro no tiene horario configurado.
const DEFAULT_CAL_START_HOUR = 8;
const DEFAULT_CAL_END_HOUR = 22;
const PX_PER_MIN = 1.2; // 60 min = 72px

function parseHHMM(s: string): number {
  const [h, m] = s.split(":").map((n) => parseInt(n, 10));
  return (h || 0) * 60 + (m || 0);
}

/** Horario del centro (Citas → Ajustes): clave = día de la semana ("0" = domingo). */
type CenterWeeklySchedule = Record<
  string,
  { isOpen: boolean; shifts: { startTime: string; endTime: string }[] }
>;

/** Horas enteras que dibujan las vistas Día y Semana. */
interface CalendarHourRange {
  startHour: number;
  endHour: number;
}

/** Minutos de un "HH:MM", o null si no lo es (no hay que estirar la agenda por un dato roto). */
function minutesOfTime(value: string | null | undefined): number | null {
  return value && /^\d{1,2}:\d{2}/.test(value) ? parseHHMM(value) : null;
}

/**
 * Rango de la agenda: de la primera apertura a la última hora de cierre del
 * horario del centro, seguido (el hueco del mediodía también se ve) y en horas
 * enteras. Si alguna cita cae fuera, el rango se amplía para no esconderla
 * nunca. Sin horario configurado: 8–22, como antes (15-oct-2026, Centro Elle:
 * abre a las 10 y la agenda pintaba dos horas grises desde las 8).
 */
function computeCalendarHourRange(
  weeklySchedule: CenterWeeklySchedule | null,
  appointments: { startTime: string; endTime: string }[],
): CalendarHourRange {
  let first = Infinity;
  let last = -Infinity;
  for (const day of Object.values(weeklySchedule ?? {})) {
    if (!day?.isOpen) continue;
    for (const shift of day.shifts ?? []) {
      const start = minutesOfTime(shift.startTime);
      const end = minutesOfTime(shift.endTime);
      if (start === null || end === null || end <= start) continue;
      first = Math.min(first, start);
      last = Math.max(last, end);
    }
  }
  if (!(last > first)) {
    first = DEFAULT_CAL_START_HOUR * 60;
    // Cierre a las 21:00 → con su franja entera, llega a las 22 como siempre.
    last = (DEFAULT_CAL_END_HOUR - 1) * 60;
  }
  for (const a of appointments) {
    const start = minutesOfTime(a.startTime);
    const end = minutesOfTime(a.endTime);
    if (start !== null) first = Math.min(first, start);
    if (end !== null && start !== null && end > start) last = Math.max(last, end);
  }
  // La franja de la hora de cierre se ve ENTERA (cierra a las 21 → la agenda
  // llega a las 22): con el borde justo en las 21 la agenda parecía cortada.
  const startHour = Math.max(0, Math.floor(first / 60));
  const endHour = Math.min(24, Math.max(startHour + 1, Math.floor(last / 60) + 1));
  return { startHour, endHour };
}

/** "HH:MM" de unos minutos desde las 00:00. */
function formatMinutesHHMM(total: number): string {
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/**
 * Cuarto de hora (minutos desde las 00:00) bajo el puntero dentro de una
 * columna de la agenda, o null fuera del rango dibujado. Es el cuarto que
 * CONTIENE al puntero, no el más cercano: el mismo que marca el recuadro
 * «+ HH:MM» al pasar el ratón.
 */
function quarterAtPointer(
  e: React.MouseEvent | React.DragEvent,
  { startHour, endHour }: CalendarHourRange,
): number | null {
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const minutes =
    Math.floor((e.clientY - rect.top) / PX_PER_MIN / 15) * 15 + startHour * 60;
  return minutes >= startHour * 60 && minutes < endHour * 60 ? minutes : null;
}

/**
 * Recuadro «+ 16:15 · Nueva cita» bajo el ratón en un hueco libre de la
 * agenda: pinchar ahí abre la cita con el día, la hora y (si se sabe) el
 * profesional ya puestos. Sin él nadie descubría que se podía (15-oct-2026).
 */
function NewAppointmentHoverSlot({
  minutes,
  dayStart,
}: {
  minutes: number;
  dayStart: number;
}) {
  return (
    <div
      className="absolute left-1 right-1 pointer-events-none rounded border border-dashed border-indigo-400 bg-indigo-50 px-1.5 text-[11px] font-medium leading-4 text-indigo-700 truncate"
      style={{ top: (minutes - dayStart) * PX_PER_MIN, height: 15 * PX_PER_MIN }}
    >
      + {formatMinutesHHMM(minutes)} · Nueva cita
    </div>
  );
}

/**
 * Reparto visual de citas solapadas dentro de una columna del calendario.
 *
 * Problema: una cita cancelada libera su hueco y otra persona reserva en el
 * mismo tramo. Al pintarse ambas a ancho completo, la cancelada tapa a la
 * válida (o al revés) según el orden del DOM. La clienta acababa moviendo la
 * cancelada a mano para poder ver la válida.
 *
 * Solución (decisión de producto 2026-08-07): la cita VÁLIDA tiene prioridad y
 * ocupa el ancho completo; una cita inactiva (cancelada / no_show) que se
 * solapa con alguna válida se encoge a una franja fina en el borde derecho
 * (sigue visible y clicable). Una cita inactiva que NO se solapa con ninguna
 * válida se pinta normal, a ancho completo. Si varias inactivas se solapan
 * entre sí, se reparten en carriles contiguos (STRIP_W cada uno).
 */
const OVERLAP_STRIP_W = 16; // px de ancho de cada franja de cita inactiva

interface OverlapLayoutInput {
  id: string;
  startTime: string;
  endTime: string;
  status: string;
}

interface OverlapLayout {
  /** ids de citas inactivas que se pintan como franja fina */
  collapsedIds: Set<string>;
  /** carril (0 = franja más pegada al borde derecho) por id colapsado */
  laneById: Map<string, number>;
  /** inset derecho en px de cada cita válida (hueco reservado a las franjas) */
  rightInsetById: Map<string, number>;
  /**
   * Citas a ANCHO COMPLETO que se pisan entre sí: carril y número de carriles
   * de su grupo, para pintarlas una al lado de la otra. Entran las válidas
   * (dos profesionales a la misma hora en la vista semana, o un profesional
   * con dos citas a la vez) y también las canceladas/no_show que NO se han
   * colapsado a franja porque no pisan ninguna válida: dos canceladas en el
   * mismo hueco se pintaban las dos a ancho completo, una encima de la otra
   * (Centro Elle, 22-sep-2026: MAYTE y ALMUDENA a las 17:00, texto tachado
   * ilegible). Antes lo mismo pasaba con dos válidas (21-sep-2026).
   */
  columnLaneById: Map<string, number>;
  columnLaneCountById: Map<string, number>;
}

function isInactiveStatus(status: string): boolean {
  return status === "cancelled" || status === "no_show";
}

function computeAppointmentOverlapLayout(
  appts: OverlapLayoutInput[],
): OverlapLayout {
  const items = appts.map((a) => ({
    id: a.id,
    start: parseHHMM(a.startTime),
    end: parseHHMM(a.endTime),
    inactive: isInactiveStatus(a.status),
  }));
  const overlaps = (
    a: { start: number; end: number },
    b: { start: number; end: number },
  ): boolean => a.start < b.end && b.start < a.end;

  const active = items.filter((i) => !i.inactive);

  // Inactivas que colisionan con alguna válida → se colapsan a franja.
  const collapsedIds = new Set<string>();
  const collapsedItems = items.filter((it) => {
    if (!it.inactive) return false;
    const collides = active.some((ac) => overlaps(ac, it));
    if (collides) collapsedIds.add(it.id);
    return collides;
  });

  // Carriles para las franjas (coloreado voraz por hora de inicio) para que
  // varias inactivas solapadas entre sí no se pisen.
  collapsedItems.sort((a, b) => a.start - b.start || a.end - b.end);
  const laneById = new Map<string, number>();
  const laneEnds: number[] = [];
  for (const it of collapsedItems) {
    let lane = laneEnds.findIndex((end) => end <= it.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(it.end);
    } else {
      laneEnds[lane] = it.end;
    }
    laneById.set(it.id, lane);
  }

  // Inset derecho de cada válida = espacio de las franjas que la solapan.
  const rightInsetById = new Map<string, number>();
  for (const ac of active) {
    let maxLane = -1;
    for (const it of collapsedItems) {
      if (overlaps(ac, it)) {
        maxLane = Math.max(maxLane, laneById.get(it.id) ?? 0);
      }
    }
    if (maxLane >= 0) {
      rightInsetById.set(ac.id, (maxLane + 1) * OVERLAP_STRIP_W + 2);
    }
  }

  // Citas a ancho completo (válidas + inactivas no colapsadas) que se pisan
  // entre sí → columnas. Se agrupan en "racimos" de citas encadenadas por
  // solape (A pisa B, B pisa C…), dentro de cada racimo se reparten carriles
  // de forma voraz por hora de inicio, y todas las del racimo comparten el
  // número de carriles para que las columnas cuadren.
  const columnLaneById = new Map<string, number>();
  const columnLaneCountById = new Map<string, number>();
  const sortedActive = items
    .filter((it) => !collapsedIds.has(it.id))
    .sort((a, b) => a.start - b.start || a.end - b.end);
  let cluster: typeof sortedActive = [];
  let clusterEnd = -1;
  const flushCluster = () => {
    if (cluster.length === 0) return;
    const ends: number[] = [];
    for (const it of cluster) {
      let lane = ends.findIndex((end) => end <= it.start);
      if (lane === -1) {
        lane = ends.length;
        ends.push(it.end);
      } else {
        ends[lane] = it.end;
      }
      columnLaneById.set(it.id, lane);
    }
    for (const it of cluster) columnLaneCountById.set(it.id, ends.length);
    cluster = [];
  };
  for (const it of sortedActive) {
    if (cluster.length > 0 && it.start >= clusterEnd) flushCluster();
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.end);
  }
  flushCluster();

  return {
    collapsedIds,
    laneById,
    rightInsetById,
    columnLaneById,
    columnLaneCountById,
  };
}

/**
 * Posición horizontal de una cita a ancho completo dentro de su columna:
 * reparte el ancho disponible (descontando el hueco de las franjas de
 * canceladas) entre los carriles de su racimo. Con un solo carril es el ancho
 * completo de antes.
 */
function columnLaneStyle(
  layout: OverlapLayout,
  id: string,
  leftPad: number,
  rightInset: number,
): { left: string; width: string } {
  const lane = layout.columnLaneById.get(id) ?? 0;
  const lanes = layout.columnLaneCountById.get(id) ?? 1;
  const available = `(100% - ${leftPad + rightInset}px)`;
  return {
    left: `calc(${leftPad}px + ${available} * ${lane} / ${lanes})`,
    width: `calc(${available} / ${lanes} - ${lanes > 1 ? 2 : 0}px)`,
  };
}

interface DayCalendarProps {
  date: Date;
  appointments: Appointment[];
  specialists: Specialist[];
  statusMeta: typeof STATUS_META;
  onSelectAppointment: (a: Appointment) => void;
  onCreateAt: (specialistId: string, time: string) => void;
  onMoveAppointment: (
    a: Appointment,
    specialistId: string,
    time: string,
  ) => void;
  getAvailability: (specialistId: string, date: Date) => DayAvailability;
  /** Liberar un hueco bloqueado pulsándolo en la agenda. */
  onBlockClick: (bloque: BloqueDeAgenda) => void;
  /** `true` si el usuario puede crear/mover citas (permiso `citas:gestionar`). */
  canManage: boolean;
  /** Horas que se dibujan (horario del centro). */
  hourRange: CalendarHourRange;
}

function DayCalendar({
  date,
  appointments,
  specialists,
  statusMeta,
  onSelectAppointment,
  onCreateAt,
  onMoveAppointment,
  getAvailability,
  onBlockClick,
  canManage,
  hourRange,
}: DayCalendarProps) {
  const { startHour, endHour } = hourRange;
  // Hueco libre bajo el ratón (profesional + minutos) para el recuadro «+ HH:MM».
  const [hoverSlot, setHoverSlot] = useState<{
    key: string;
    minutes: number;
  } | null>(null);
  const totalMinutes = (endHour - startHour) * 60;
  const totalHeight = totalMinutes * PX_PER_MIN;
  const hours = Array.from(
    { length: endHour - startHour + 1 },
    (_, i) => startHour + i,
  );

  // Línea de "ahora" si es hoy
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const showNowLine =
    nowMinutes >= startHour * 60 && nowMinutes <= endHour * 60;
  const nowTop = (nowMinutes - startHour * 60) * PX_PER_MIN;

  if (specialists.length === 0) {
    return (
      <div className="p-12 text-center text-gray-500">
        <UserIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="font-medium text-gray-700">No hay profesionales</p>
        <p className="text-sm mt-1">
          Crea al menos un profesional para ver el calendario.
        </p>
      </div>
    );
  }

  // Solo scroll LATERAL (muchos profesionales). Con `overflow-auto` la última
  // etiqueta de hora asomaba unos px y la caja también se movía en vertical:
  // la rueda del ratón se quedaba enganchada en ella en vez de mover la página.
  // `mt-2` deja sitio a la primera etiqueta y `mb-4`, aire bajo la última franja.
  return (
    <div className="overflow-x-auto overflow-y-hidden">
      <div
        className="relative"
        style={{
          minWidth: 80 + specialists.length * 180,
        }}
      >
        {/* Cabecera de profesionales */}
        <div className="sticky top-0 z-20 flex bg-white border-b border-gray-200">
          <div className="w-20 flex-shrink-0 border-r border-gray-200" />
          {specialists.map((sp) => (
            <div
              key={sp.id}
              className="flex-1 min-w-[180px] px-2 py-2 border-r border-gray-200 last:border-r-0"
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[11px] font-semibold"
                  style={{ backgroundColor: sp.color }}
                >
                  {sp.firstName.charAt(0).toUpperCase()}
                </div>
                <div className="text-sm font-medium text-gray-800 truncate">
                  {sp.firstName} {sp.lastName ?? ""}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Cuerpo: timeline */}
        <div className="relative flex mt-2 mb-4" style={{ height: totalHeight }}>
          {/* Columna de horas */}
          <div className="w-20 flex-shrink-0 border-r border-gray-200 relative">
            {hours.slice(0, -1).map((h) => (
              <div
                key={h}
                className="absolute left-0 right-0 px-2 text-[11px] text-gray-500"
                style={{
                  top: (h - startHour) * 60 * PX_PER_MIN,
                  transform: "translateY(-6px)",
                }}
              >
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>

          {/* Líneas horarias horizontales (en el contenedor) */}
          <div className="flex-1 relative flex">
            {/* Background grid */}
            <div className="absolute inset-0 pointer-events-none">
              {hours.map((h) => (
                <div
                  key={h}
                  className="absolute left-0 right-0 border-t border-gray-200"
                  style={{ top: (h - startHour) * 60 * PX_PER_MIN }}
                />
              ))}
              {hours.slice(0, -1).map((h) => (
                <div
                  key={`half-${h}`}
                  className="absolute left-0 right-0 border-t border-dashed border-gray-100"
                  style={{
                    top:
                      (h - startHour) * 60 * PX_PER_MIN + 30 * PX_PER_MIN,
                  }}
                />
              ))}
            </div>

            {/* Línea AHORA */}
            {showNowLine && (
              <div
                className="absolute left-0 right-0 z-10 pointer-events-none"
                style={{ top: nowTop }}
              >
                <div className="h-px bg-red-500" />
                <div className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-red-500" />
              </div>
            )}

            {/* Columnas por profesional */}
            {specialists.map((sp) => {
              const cellAppointments = appointments.filter(
                (a) => a.specialist.id === sp.id,
              );
              // Reparto de solapes: las canceladas/no_show que pisan una cita
              // válida se encogen a franja; la válida manda a ancho completo.
              const overlapLayout =
                computeAppointmentOverlapLayout(cellAppointments);
              const av = getAvailability(sp.id, date);
              // Calcula zonas NO trabajables como complemento de los shifts
              const dayStart = startHour * 60;
              const dayEnd = endHour * 60;
              const workRanges: { start: number; end: number }[] = av.shifts
                .map((s) => ({
                  start: Math.max(dayStart, parseHHMM(s.start)),
                  end: Math.min(dayEnd, parseHHMM(s.end)),
                }))
                .filter((r) => r.end > r.start)
                .sort((a, b) => a.start - b.start);
              const offRanges: { start: number; end: number }[] = [];
              if (!av.isWorking || workRanges.length === 0) {
                offRanges.push({ start: dayStart, end: dayEnd });
              } else {
                let cursor = dayStart;
                for (const r of workRanges) {
                  if (r.start > cursor)
                    offRanges.push({ start: cursor, end: r.start });
                  cursor = Math.max(cursor, r.end);
                }
                if (cursor < dayEnd)
                  offRanges.push({ start: cursor, end: dayEnd });
              }
              const computeTimeFromY = (
                e: React.MouseEvent | React.DragEvent,
              ): string | null => {
                const minutes = quarterAtPointer(e, hourRange);
                return minutes === null ? null : formatMinutesHHMM(minutes);
              };
              // Hueco libre = dentro de su turno y sin nada encima (una cita
              // o un bloqueo son hijos de la columna: ahí el objetivo cambia).
              const freeQuarterAt = (e: React.MouseEvent): number | null => {
                if (!canManage || e.target !== e.currentTarget) return null;
                const minutes = quarterAtPointer(e, hourRange);
                if (minutes === null) return null;
                return workRanges.some(
                  (r) => minutes >= r.start && minutes < r.end,
                )
                  ? minutes
                  : null;
              };
              const isHovered = hoverSlot?.key === sp.id;
              return (
                <div
                  key={sp.id}
                  className={`flex-1 min-w-[180px] border-r border-gray-200 last:border-r-0 relative ${isHovered ? "cursor-pointer" : ""}`}
                  onMouseMove={(e) => {
                    const minutes = freeQuarterAt(e);
                    if (minutes === null) {
                      if (hoverSlot) setHoverSlot(null);
                    } else if (!isHovered || hoverSlot?.minutes !== minutes) {
                      setHoverSlot({ key: sp.id, minutes });
                    }
                  }}
                  onMouseLeave={() => setHoverSlot(null)}
                  onClick={(e) => {
                    // Solo en zona laborable y libre (lo mismo que marca el recuadro).
                    const minutes = freeQuarterAt(e);
                    if (minutes === null) return;
                    setHoverSlot(null);
                    onCreateAt(sp.id, formatMinutesHHMM(minutes));
                  }}
                  onDragOver={(e) => {
                    if (!canManage) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => {
                    if (!canManage) return;
                    e.preventDefault();
                    const id = e.dataTransfer.getData("text/x-appointment-id");
                    if (!id) return;
                    const a = appointments.find((x) => x.id === id);
                    if (!a) return;
                    const time = computeTimeFromY(e);
                    if (!time) return;
                    if (a.specialist.id === sp.id && a.startTime === time)
                      return;
                    onMoveAppointment(a, sp.id, time);
                  }}
                >
                  {/* Overlays de zonas no laborables (rayadas) */}
                  {offRanges.map((r, idx) => (
                    <div
                      key={`off-${idx}`}
                      className="absolute left-0 right-0 pointer-events-none bg-gray-200/60"
                      style={{
                        top: (r.start - dayStart) * PX_PER_MIN,
                        height: (r.end - r.start) * PX_PER_MIN,
                        backgroundImage:
                          "repeating-linear-gradient(45deg, transparent 0 6px, rgba(0,0,0,0.04) 6px 12px)",
                      }}
                      title="No disponible"
                    />
                  ))}
                  {isHovered && hoverSlot && (
                    <NewAppointmentHoverSlot
                      minutes={hoverSlot.minutes}
                      dayStart={dayStart}
                    />
                  )}
                  {/* Overlays de bloqueos puntuales */}
                  {av.blocks.map((b, idx) => {
                    const bs = parseHHMM(b.startTime);
                    const be = parseHHMM(b.endTime);
                    if (be <= dayStart || bs >= dayEnd) return null;
                    return (
                      <button
                        key={`bl-${b.id ?? idx}`}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canManage && b.id) onBlockClick(b);
                        }}
                        disabled={!canManage || !b.id}
                        className="absolute left-0 right-0 text-left bg-red-100/70 border-l-2 border-red-400 overflow-hidden px-1 disabled:cursor-default"
                        style={{
                          top: (Math.max(dayStart, bs) - dayStart) * PX_PER_MIN,
                          height:
                            (Math.min(dayEnd, be) - Math.max(dayStart, bs)) *
                            PX_PER_MIN,
                        }}
                        title={`Hueco bloqueado ${b.startTime}–${b.endTime}${b.reason ? ` · ${b.reason}` : ""}${
                          b.authorName ? ` · por ${b.authorName}` : ""
                        }${canManage && b.id ? " · pulsa para liberarlo" : ""}`}
                      >
                        <span className="block text-[10px] leading-tight text-red-800 font-medium truncate">
                          {b.reason || "Bloqueado"}
                        </span>
                        {b.authorName && (
                          <span className="block text-[9px] leading-tight text-red-700/80 truncate">
                            por {b.authorName}
                          </span>
                        )}
                      </button>
                    );
                  })}{" "}
                  {cellAppointments.map((a) => {
                    const startMin = parseHHMM(a.startTime);
                    const endMin = parseHHMM(a.endTime);
                    const top = (startMin - startHour * 60) * PX_PER_MIN;
                    const height = Math.max(
                      20,
                      (endMin - startMin) * PX_PER_MIN - 2,
                    );
                    if (
                      startMin >= endHour * 60 ||
                      endMin <= startHour * 60
                    )
                      return null;
                    const meta = statusMeta[a.status];
                    const isCancelled = a.status === "cancelled";
                    const isNoShow = a.status === "no_show";
                    // Cita inactiva que pisa una válida → franja fina a la
                    // derecha; la válida reserva ese hueco por su derecha.
                    const isCollapsed = overlapLayout.collapsedIds.has(a.id);
                    const lane = overlapLayout.laneById.get(a.id) ?? 0;
                    const rightInset = overlapLayout.rightInsetById.get(a.id);
                    const fullName =
                      `${a.customer.firstName} ${a.customer.lastName ?? ""}`.trim();
                    const bookingName = differingBookingName(a);
                    return (
                      <button
                        key={a.id}
                        draggable={
                          canManage &&
                          !isCancelled &&
                          !isNoShow &&
                          a.status !== "completed"
                        }
                        onDragStart={(e) => {
                          if (!canManage) return;
                          e.dataTransfer.setData("text/x-appointment-id", a.id);
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectAppointment(a);
                        }}
                        className={`absolute text-left rounded-md text-[11px] leading-tight overflow-hidden border shadow-sm hover:shadow-md transition-shadow ${
                          isCollapsed ? "p-0" : "p-1.5"
                        } ${
                          isCancelled || isNoShow
                            ? "opacity-60 line-through"
                            : ""
                        }`}
                        style={{
                          top,
                          height,
                          ...(isCollapsed
                            ? {
                                right: 2 + lane * OVERLAP_STRIP_W,
                                width: OVERLAP_STRIP_W - 3,
                                zIndex: 1,
                              }
                            : {
                                ...columnLaneStyle(overlapLayout, a.id, 4, rightInset ?? 4),
                                zIndex: 2,
                              }),
                          backgroundColor: a.service.color + "22",
                          borderLeft: `3px solid ${a.service.color}`,
                          borderRight: a.appointmentRoom
                            ? `3px solid ${a.appointmentRoom.color}`
                            : undefined,
                          borderTopColor: "rgba(0,0,0,0.05)",
                          borderRightColor: a.appointmentRoom
                            ? a.appointmentRoom.color
                            : "rgba(0,0,0,0.05)",
                          borderBottomColor: "rgba(0,0,0,0.05)",
                        }}
                        title={`${a.startTime}-${a.endTime} · ${a.service.name} · ${bookingName ?? fullName}${bookingName ? ` (reserva: ${fullName})` : ""}${a.appointmentRoom ? ` · 🚪 ${a.appointmentRoom.name}` : ""}${isCollapsed ? " · (cancelada)" : ""}${a.pendingChangeRequest ? " · ⏰ Cambio pendiente de decidir" : ""}`}
                      >
                        {isCollapsed ? null : (
                          <>
                        {a.appointmentRoom && (
                          <div
                            className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full ring-1 ring-white"
                            style={{
                              backgroundColor: a.appointmentRoom.color,
                            }}
                          />
                        )}
                        {a.pendingChangeRequest && (
                          <div className="absolute top-0.5 left-0.5 w-2 h-2 rounded-full ring-1 ring-white bg-amber-500 animate-pulse" />
                        )}
                        <div className="font-semibold text-gray-900 truncate pr-3">
                          {a.startTime} {bookingName ?? (fullName || "Cliente")}
                        </div>
                        <div className="text-gray-700 truncate">
                          {a.service.name}
                          {/* En la agenda manda quien RECIBE la cita (arriba);
                              en la línea del servicio va quién la reservó, como
                              contacto secundario. */}
                          {bookingName && (
                            <span className="ml-1 text-gray-500">
                              · reserva: {fullName || "Cliente"}
                            </span>
                          )}
                        </div>
                        {height > 44 && a.appointmentRoom && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <span
                              className="text-[9px] px-1 rounded truncate"
                              style={{
                                backgroundColor: a.appointmentRoom.color + "33",
                                color: a.appointmentRoom.color,
                              }}
                            >
                              🚪 {a.appointmentRoom.name}
                            </span>
                          </div>
                        )}
                        {height > 60 && meta && (
                          <div
                            className={`inline-block mt-0.5 px-1 rounded text-[10px] ${meta.bg} ${meta.text}`}
                          >
                            {meta.label}
                          </div>
                        )}
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// =================== Vista SEMANA ===================

interface WeekCalendarProps {
  weekStart: Date;
  appointments: Appointment[];
  specialists: Specialist[];
  statusMeta: typeof STATUS_META;
  onSelectAppointment: (a: Appointment) => void;
  onSelectDay: (date: Date) => void;
  onCreateAt?: (date: Date, specialistId: string, time: string) => void;
  onMoveAppointment?: (a: Appointment, date: Date, time: string) => void;
  getAvailability: (specialistId: string, date: Date) => DayAvailability;
  /** Horas que se dibujan (horario del centro). */
  hourRange: CalendarHourRange;
}

function WeekCalendar({
  weekStart,
  appointments,
  specialists,
  statusMeta,
  onSelectAppointment,
  onSelectDay,
  onCreateAt,
  onMoveAppointment,
  getAvailability,
  hourRange,
}: WeekCalendarProps) {
  const { startHour, endHour } = hourRange;
  // Hueco libre bajo el ratón (día + minutos) para el recuadro «+ HH:MM».
  const [hoverSlot, setHoverSlot] = useState<{
    key: string;
    minutes: number;
  } | null>(null);
  const totalMinutes = (endHour - startHour) * 60;
  const totalHeight = totalMinutes * PX_PER_MIN;
  const hours = Array.from(
    { length: endHour - startHour + 1 },
    (_, i) => startHour + i,
  );
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const showNowLine =
    nowMinutes >= startHour * 60 && nowMinutes <= endHour * 60;
  const nowTop = (nowMinutes - startHour * 60) * PX_PER_MIN;

  // Mapa fecha (YYYY-MM-DD) -> citas
  const byDay = new Map<string, Appointment[]>();
  for (const a of appointments) {
    // a.date es ISO con tiempo; lo normalizamos a YYYY-MM-DD local
    const d = new Date(a.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const arr = byDay.get(key) ?? [];
    arr.push(a);
    byDay.set(key, arr);
  }

  // Para citas en vista semana, sólo mostramos el primer specialist filtrado
  // (si no, sería ilegible). Si el usuario quiere ver por profesional, que use vista día.
  const todayDate = new Date();

  return (
    <div className="overflow-x-auto overflow-y-hidden">
      {/* Leyenda de colores de profesionales */}
      {specialists.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 px-3 py-2 border-b border-gray-200 bg-gray-50">
          <span className="text-[11px] uppercase tracking-wide text-gray-500">
            Profesionales:
          </span>
          {specialists.map((sp) => (
            <span
              key={sp.id}
              className="inline-flex items-center gap-1.5 text-xs text-gray-700"
            >
              <span
                className="inline-block w-3 h-3 rounded-full border border-white shadow-sm"
                style={{ backgroundColor: sp.color }}
              />
              {sp.firstName} {sp.lastName ?? ""}
            </span>
          ))}
        </div>
      )}
      <div className="relative" style={{ minWidth: 80 + 7 * 140 }}>
        {/* Cabecera de días */}
        <div className="sticky top-0 z-20 flex bg-white border-b border-gray-200">
          <div className="w-20 flex-shrink-0 border-r border-gray-200" />
          {days.map((d) => {
            const isToday = isSameDate(d, todayDate);
            return (
              <button
                key={d.toISOString()}
                type="button"
                onClick={() => onSelectDay(d)}
                className={`flex-1 min-w-[140px] px-2 py-2 border-r border-gray-200 last:border-r-0 text-center hover:bg-indigo-50 transition-colors ${isToday ? "bg-indigo-50" : ""}`}
                title="Ver este día"
              >
                <div className="text-[11px] uppercase tracking-wide text-gray-500">
                  {d.toLocaleDateString("es-ES", { weekday: "short" })}
                </div>
                <div
                  className={`text-sm font-semibold ${isToday ? "text-indigo-600" : "text-gray-800"}`}
                >
                  {d.getDate()}{" "}
                  {d.toLocaleDateString("es-ES", { month: "short" })}
                </div>
              </button>
            );
          })}
        </div>

        {/* Cuerpo */}
        <div className="relative flex mt-2 mb-4" style={{ height: totalHeight }}>
          {/* Columna horas */}
          <div className="w-20 flex-shrink-0 border-r border-gray-200 relative">
            {hours.slice(0, -1).map((h) => (
              <div
                key={h}
                className="absolute left-0 right-0 px-2 text-[11px] text-gray-500"
                style={{
                  top: (h - startHour) * 60 * PX_PER_MIN,
                  transform: "translateY(-6px)",
                }}
              >
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>

          <div className="flex-1 relative flex">
            {/* Background grid */}
            <div className="absolute inset-0 pointer-events-none">
              {hours.map((h) => (
                <div
                  key={h}
                  className="absolute left-0 right-0 border-t border-gray-200"
                  style={{ top: (h - startHour) * 60 * PX_PER_MIN }}
                />
              ))}
            </div>

            {/* Línea AHORA solo si hoy está en el rango */}
            {showNowLine && days.some((d) => isSameDate(d, todayDate)) && (
              <div
                className="absolute left-0 right-0 z-10 pointer-events-none"
                style={{ top: nowTop }}
              >
                <div className="h-px bg-red-500 opacity-40" />
              </div>
            )}

            {days.map((d) => {
              const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              const dayAppointments = byDay.get(key) ?? [];
              // Mismo reparto de solapes que la vista día: la válida a ancho
              // completo, la cancelada/no_show que la pisa a franja fina.
              const overlapLayout =
                computeAppointmentOverlapLayout(dayAppointments);
              const isToday = isSameDate(d, todayDate);
              const dayStart = startHour * 60;
              const dayEnd = endHour * 60;
              // Solo pintamos overlays si hay 1 profesional visible
              const onlySp = specialists.length === 1 ? specialists[0] : null;
              const av = onlySp ? getAvailability(onlySp.id, d) : null;
              const offRanges: { start: number; end: number }[] = [];
              if (av) {
                const work = av.shifts
                  .map((s) => ({
                    start: Math.max(dayStart, parseHHMM(s.start)),
                    end: Math.min(dayEnd, parseHHMM(s.end)),
                  }))
                  .filter((r) => r.end > r.start)
                  .sort((a, b) => a.start - b.start);
                if (!av.isWorking || work.length === 0) {
                  offRanges.push({ start: dayStart, end: dayEnd });
                } else {
                  let cursor = dayStart;
                  for (const r of work) {
                    if (r.start > cursor)
                      offRanges.push({ start: cursor, end: r.start });
                    cursor = Math.max(cursor, r.end);
                  }
                  if (cursor < dayEnd)
                    offRanges.push({ start: cursor, end: dayEnd });
                }
              }
              // Hueco libre: sin nada encima y, si hay UN profesional visible,
              // dentro de su turno. Con todos visibles no se sabe de quién es
              // el hueco: la ventana rápida pide el profesional.
              const freeQuarterAt = (e: React.MouseEvent): number | null => {
                if (!onCreateAt || specialists.length === 0) return null;
                if (e.target !== e.currentTarget) return null;
                const minutes = quarterAtPointer(e, hourRange);
                if (minutes === null) return null;
                return offRanges.some(
                  (r) => minutes >= r.start && minutes < r.end,
                )
                  ? null
                  : minutes;
              };
              const isHovered = hoverSlot?.key === key;
              return (
                <div
                  key={key}
                  className={`flex-1 min-w-[140px] border-r border-gray-200 last:border-r-0 relative ${isToday ? "bg-indigo-50/30" : ""} ${isHovered ? "cursor-pointer" : ""}`}
                  onMouseMove={(e) => {
                    const minutes = freeQuarterAt(e);
                    if (minutes === null) {
                      if (hoverSlot) setHoverSlot(null);
                    } else if (!isHovered || hoverSlot?.minutes !== minutes) {
                      setHoverSlot({ key, minutes });
                    }
                  }}
                  onMouseLeave={() => setHoverSlot(null)}
                  onClick={(e) => {
                    const minutes = freeQuarterAt(e);
                    if (minutes === null || !onCreateAt) return;
                    setHoverSlot(null);
                    onCreateAt(
                      d,
                      onlySp ? onlySp.id : "",
                      formatMinutesHHMM(minutes),
                    );
                  }}
                  onDragOver={(e) => {
                    if (!onMoveAppointment) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => {
                    if (!onMoveAppointment) return;
                    e.preventDefault();
                    const id = e.dataTransfer.getData("text/x-appointment-id");
                    if (!id) return;
                    const a = appointments.find((x) => x.id === id);
                    if (!a) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const y = e.clientY - rect.top;
                    const totalMin =
                      Math.round(y / PX_PER_MIN / 15) * 15 +
                      startHour * 60;
                    const h = Math.floor(totalMin / 60);
                    const m = totalMin % 60;
                    if (h < startHour || h >= endHour) return;
                    const time = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                    onMoveAppointment(a, d, time);
                  }}
                >
                  {/* Overlays no laborables (solo si hay 1 profesional visible) */}
                  {offRanges.map((r, idx) => (
                    <div
                      key={`woff-${idx}`}
                      className="absolute left-0 right-0 pointer-events-none bg-gray-200/60"
                      style={{
                        top: (r.start - dayStart) * PX_PER_MIN,
                        height: (r.end - r.start) * PX_PER_MIN,
                        backgroundImage:
                          "repeating-linear-gradient(45deg, transparent 0 6px, rgba(0,0,0,0.04) 6px 12px)",
                      }}
                    />
                  ))}
                  {isHovered && hoverSlot && (
                    <NewAppointmentHoverSlot
                      minutes={hoverSlot.minutes}
                      dayStart={dayStart}
                    />
                  )}
                  {av?.blocks.map((b, idx) => {
                    const bs = parseHHMM(b.startTime);
                    const be = parseHHMM(b.endTime);
                    if (be <= dayStart || bs >= dayEnd) return null;
                    return (
                      <div
                        key={`wbl-${idx}`}
                        className="absolute left-0 right-0 pointer-events-none bg-red-100/70 border-l-2 border-red-400"
                        style={{
                          top: (Math.max(dayStart, bs) - dayStart) * PX_PER_MIN,
                          height:
                            (Math.min(dayEnd, be) - Math.max(dayStart, bs)) *
                            PX_PER_MIN,
                        }}
                        title={b.reason || "Bloqueo"}
                      />
                    );
                  })}{" "}
                  {dayAppointments.map((a) => {
                    const startMin = parseHHMM(a.startTime);
                    const endMin = parseHHMM(a.endTime);
                    const top = (startMin - startHour * 60) * PX_PER_MIN;
                    const height = Math.max(
                      18,
                      (endMin - startMin) * PX_PER_MIN - 2,
                    );
                    if (
                      startMin >= endHour * 60 ||
                      endMin <= startHour * 60
                    )
                      return null;
                    const meta = statusMeta[a.status];
                    const isCancelled = a.status === "cancelled";
                    const isNoShow = a.status === "no_show";
                    // Cita inactiva que pisa una válida → franja fina a la
                    // derecha; la válida reserva ese hueco por su derecha.
                    const isCollapsed = overlapLayout.collapsedIds.has(a.id);
                    const lane = overlapLayout.laneById.get(a.id) ?? 0;
                    const rightInset = overlapLayout.rightInsetById.get(a.id);
                    const fullName =
                      `${a.customer.firstName} ${a.customer.lastName ?? ""}`.trim();
                    // Semana: solo en el tooltip. Con 7 columnas la tarjeta es
                    // demasiado estrecha (~134 px en un portátil) y la etiqueta
                    // se quedaba en "A nombre de:…" sin enseñar el nombre; el
                    // detalle (clic) sí lo muestra completo.
                    const bookingName = differingBookingName(a);
                    return (
                      <button
                        key={a.id}
                        draggable={
                          !!onMoveAppointment &&
                          !isCancelled &&
                          !isNoShow &&
                          a.status !== "completed"
                        }
                        onDragStart={(e) => {
                          if (!onMoveAppointment) return;
                          e.dataTransfer.setData("text/x-appointment-id", a.id);
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectAppointment(a);
                        }}
                        className={`absolute text-left rounded text-[10px] leading-tight overflow-hidden border shadow-sm hover:shadow-md transition-shadow ${
                          isCollapsed ? "p-0" : "p-1"
                        } ${
                          isCancelled || isNoShow
                            ? "opacity-60 line-through"
                            : ""
                        }`}
                        style={{
                          top,
                          height,
                          ...(isCollapsed
                            ? {
                                right: 2 + lane * OVERLAP_STRIP_W,
                                width: OVERLAP_STRIP_W - 3,
                                zIndex: 1,
                              }
                            : {
                                ...columnLaneStyle(overlapLayout, a.id, 2, rightInset ?? 2),
                                zIndex: 2,
                              }),
                          backgroundColor: a.specialist.color + "33",
                          borderLeft: `3px solid ${a.specialist.color}`,
                          borderRight: a.appointmentRoom
                            ? `3px solid ${a.appointmentRoom.color}`
                            : undefined,
                          borderTopColor: "rgba(0,0,0,0.05)",
                          borderRightColor: a.appointmentRoom
                            ? a.appointmentRoom.color
                            : "rgba(0,0,0,0.05)",
                          borderBottomColor: "rgba(0,0,0,0.05)",
                        }}
                        title={`${a.startTime}-${a.endTime} · ${a.service.name} · ${bookingName ?? fullName}${bookingName ? ` (reserva: ${fullName})` : ""} · ${a.specialist.firstName}${a.appointmentRoom ? ` · 🚪 ${a.appointmentRoom.name}` : ""}${isCollapsed ? " · (cancelada)" : ""}${a.pendingChangeRequest ? " · ⏰ Cambio pendiente de decidir" : ""}`}
                      >
                        {isCollapsed ? null : (
                          <>
                        {a.appointmentRoom && (
                          <div
                            className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full ring-1 ring-white"
                            style={{
                              backgroundColor: a.appointmentRoom.color,
                            }}
                          />
                        )}
                        {a.pendingChangeRequest && (
                          <div className="absolute top-0.5 left-0.5 w-2 h-2 rounded-full ring-1 ring-white bg-amber-500 animate-pulse" />
                        )}
                        <div className="font-semibold text-gray-900 truncate pr-3">
                          {a.startTime} {bookingName ?? (fullName || "Cliente")}
                        </div>
                        {height > 28 && (
                          <div className="text-gray-700 truncate">
                            {a.service.name}
                            {bookingName && (
                              <span className="ml-1 text-gray-500">
                                · reserva: {fullName || "Cliente"}
                              </span>
                            )}
                          </div>
                        )}
                        {height > 50 && meta && (
                          <div
                            className={`inline-block mt-0.5 px-1 rounded text-[9px] ${meta.bg} ${meta.text}`}
                          >
                            {meta.label}
                          </div>
                        )}
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// =================== Vista MES ===================

interface MonthCalendarProps {
  month: Date;
  appointments: Appointment[];
  specialists: Specialist[];
  statusMeta: typeof STATUS_META;
  onSelectDay: (date: Date) => void;
}

function MonthCalendar({
  month,
  appointments,
  specialists: _specialists,
  statusMeta: _statusMeta,
  onSelectDay,
}: MonthCalendarProps) {
  // Construir matriz de semanas (lunes como inicio)
  const first = startOfMonth(month);
  const last = endOfMonth(month);
  const gridStart = startOfWeek(first);
  const gridEndCandidate = startOfWeek(last);
  const gridEnd = addDays(gridEndCandidate, 6);
  const totalDays =
    Math.round((gridEnd.getTime() - gridStart.getTime()) / 86400000) + 1;
  const days: Date[] = Array.from({ length: totalDays }, (_, i) =>
    addDays(gridStart, i),
  );

  // Mapa fecha (YYYY-MM-DD) -> citas
  const byDay = new Map<string, Appointment[]>();
  for (const a of appointments) {
    const d = new Date(a.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const arr = byDay.get(key) ?? [];
    arr.push(a);
    byDay.set(key, arr);
  }

  const today = new Date();
  const weekdayLabels = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  return (
    <div>
      {/* Cabecera de días de la semana */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {weekdayLabels.map((w) => (
          <div
            key={w}
            className="px-2 py-2 text-[11px] uppercase tracking-wide text-gray-500 text-center"
          >
            {w}
          </div>
        ))}
      </div>

      {/* Cuadrícula de días */}
      <div className="grid grid-cols-7 auto-rows-fr">
        {days.map((d) => {
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          const dayAppointments = (byDay.get(key) ?? [])
            .slice()
            .sort((a, b) => a.startTime.localeCompare(b.startTime));
          const isCurrentMonth = d.getMonth() === month.getMonth();
          const isToday = isSameDate(d, today);
          const visibleCount = 3;
          const overflow = Math.max(0, dayAppointments.length - visibleCount);

          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelectDay(d)}
              className={`relative text-left min-h-[110px] p-1.5 border-r border-b border-gray-200 last:border-r-0 hover:bg-indigo-50 transition-colors ${
                isCurrentMonth ? "bg-white" : "bg-gray-50/60 text-gray-400"
              } ${isToday ? "ring-2 ring-inset ring-indigo-300" : ""}`}
              title={`Ver ${d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-xs font-semibold ${
                    isToday
                      ? "text-indigo-600"
                      : isCurrentMonth
                        ? "text-gray-800"
                        : "text-gray-400"
                  }`}
                >
                  {d.getDate()}
                </span>
                {dayAppointments.length > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">
                    {dayAppointments.length}
                  </span>
                )}
              </div>
              <div className="space-y-0.5">
                {dayAppointments.slice(0, visibleCount).map((a) => {
                  const isCancelled = a.status === "cancelled";
                  const isNoShow = a.status === "no_show";
                  const fullName =
                    `${a.customer.firstName} ${a.customer.lastName ?? ""}`.trim();
                  return (
                    <div
                      key={a.id}
                      className={`relative text-[10px] leading-tight px-1 py-0.5 rounded truncate ${
                        isCancelled || isNoShow ? "opacity-60 line-through" : ""
                      }`}
                      style={{
                        backgroundColor: a.specialist.color + "33",
                        borderLeft: `3px solid ${a.specialist.color}`,
                        borderRight: a.appointmentRoom
                          ? `3px solid ${a.appointmentRoom.color}`
                          : undefined,
                      }}
                      title={`${a.startTime} · ${a.service.name} · ${fullName}${a.appointmentRoom ? ` · 🚪 ${a.appointmentRoom.name}` : ""}${a.pendingChangeRequest ? " · ⏰ Cambio pendiente de decidir" : ""}`}
                    >
                      {a.pendingChangeRequest && (
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 mr-1 align-middle animate-pulse" />
                      )}
                      <span className="font-semibold">{a.startTime}</span>{" "}
                      {fullName || "Cliente"}
                    </div>
                  );
                })}
                {overflow > 0 && (
                  <div className="text-[10px] text-gray-500 px-1">
                    +{overflow} más
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
